import React, { Component } from 'react'
import {
    Card, Table, Button, Drawer, Form, Input, Select, Space, message,
    Popconfirm, Typography, Row, Col, Tag, Spin, Modal, Alert, InputNumber, Switch, AutoComplete
} from 'antd'
import {
    PlusOutlined, EditOutlined, DeleteOutlined, RocketOutlined, ReloadOutlined,
    ThunderboltOutlined, CheckCircleOutlined, StopOutlined, CodeOutlined,
    BugOutlined, CopyOutlined, ApiOutlined
} from '@ant-design/icons'
import {
    getMcpToolList, saveMcpTool, updateMcpTool,
    saveMcpToolVersion, publishMcpToolVersion, getMcpToolVersionList,
    saveApiRegistry, updateApiRegistry, getApiRegistryList, createToolCombo,
    testMcpToolVersion, validateMcpToolVersion, getMcpServiceList, getMcpToolPublishedSpec,
    debugManagedMcpToolRequest
} from '../../request'

const { Text } = Typography
const { TextArea } = Input

// 极大简化的极客一站式工作台
export default class McpTool extends Component {
    formRef = React.createRef()
    handledEntrySearch = ''
    submitIntent = 'publish'
    _jsonSchemaTimer = null
    _jsonOutputTimer = null
    
    generateId = (prefix = 'id') => `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`


    state = {
        dataSource: [],
        apiDataSource: [],
        loading: false,
        saving: false,
        drawerVisible: false,
        editRecord: null,
        parsedParams: [],
        parsedOutputs: [],
        // 测试相关
        testing: false,
        testingToolId: null,
        testResult: null,
        testModalVisible: false,
        testTargetRecord: null,
        debugging: false,
        debugResult: null,
        managedDebugRequired: false,
        lastDebugSignature: null,
        activeEditorSection: 'tool-info',
        toolDebugVisible: false,
        toolDebugInputMode: 'form',
        toolDebugCodeInput: '{}',
        toolDebugParamValues: {},
        toolDebugRunResult: null,
        toolDebugStatusByToolId: {},
        toolDebugTimeByToolId: {},
        // 发布成功连接信息
        publishInfoVisible: false,
        publishedTool: null,
        // 服务信息
        serviceInfo: null,
        inputSchemaEmptyObject: false,
        jsonSchemaImportVisible: false,
        jsonOutputImportVisible: false,
        selectedApiCode: undefined,
        apiMode: 'bind_existing',
        formSnapshot: {},
        fixedHeaders: [],
        responseMappings: [],
        examples: [],
        specPreviewVisible: false,
        specPreviewLoading: false,
        specPreviewData: null
    }

    componentDidMount() {
        this.loadList()
        this.loadApiList()
        this.loadServiceInfo()
        this.loadToolDebugCache()
    }

    componentDidUpdate(prevProps, prevState) {
        const prevSearch = prevProps.location?.search || ''
        const currentSearch = this.getCurrentSearch()

        if (prevSearch !== currentSearch) {
            this.handledEntrySearch = ''
            this.loadList()
            this.loadApiList()
            this.loadServiceInfo()
            return
        }

        if (
            prevState.dataSource !== this.state.dataSource
            || prevState.apiDataSource !== this.state.apiDataSource
        ) {
            this.handleEntryQuery()
        }

        if (prevState.parsedOutputs !== this.state.parsedOutputs) {
            const nextResponseMappings = this.buildResponseMappingsFromOutputs(this.state.parsedOutputs)
            if (!this.areResponseMappingsEqual(nextResponseMappings, this.state.responseMappings)) {
                this.setState({ responseMappings: nextResponseMappings })
            }
        }
    }

    getCurrentSearch = () => this.props.location?.search || window.location.search || ''

    getQueryParams = () => {
        const params = new URLSearchParams(this.getCurrentSearch())
        const serviceId = params.get('serviceId')
        const toolId = params.get('toolId')
        const apiCode = params.get('apiCode')
        return {
            serviceId: serviceId ? Number(serviceId) : undefined,
            toolId: toolId ? Number(toolId) : undefined,
            apiCode: apiCode || undefined,
        }
    }

    getServiceId = () => {
        return this.getQueryParams().serviceId
    }

    getToolDebugStorageKey = () => `mcp_tool_debug_status_${this.getServiceId() || 'global'}`

    loadToolDebugCache = () => {
        try {
            const raw = window.localStorage.getItem(this.getToolDebugStorageKey())
            if (!raw) return
            const parsed = JSON.parse(raw)
            this.setState({
                toolDebugStatusByToolId: parsed?.statusByToolId || {},
                toolDebugTimeByToolId: parsed?.timeByToolId || {}
            })
        } catch (e) {
            // ignore invalid cache
        }
    }

    persistToolDebugCache = (statusByToolId, timeByToolId) => {
        try {
            window.localStorage.setItem(this.getToolDebugStorageKey(), JSON.stringify({
                statusByToolId,
                timeByToolId
            }))
        } catch (e) {
            // ignore storage errors
        }
    }

    goToApiRegistry = () => {
        this.props.history.push('/admin/mcpapi')
    }

    handleEntryQuery = () => {
        const currentSearch = this.getCurrentSearch()
        if (!currentSearch || this.handledEntrySearch === currentSearch) return

        const { toolId, apiCode } = this.getQueryParams()

        if (toolId) {
            const targetTool = this.state.dataSource.find(item => String(item.id) === String(toolId))
            if (!targetTool) return
            this.handledEntrySearch = currentSearch
            this.openEdit(targetTool)
            return
        }

        if (apiCode) {
            const targetApi = this.state.apiDataSource.find(item => item.apiCode === apiCode)
            if (!targetApi) return
            this.handledEntrySearch = currentSearch
            this.openCreate(() => this.handleApiSelect(apiCode))
        }
    }

    loadList = () => {
        this.setState({ loading: true })
        const serviceId = this.getServiceId()
        return getMcpToolList(serviceId ? { serviceId } : {})
            .then(res => this.setState({ dataSource: Array.isArray(res) ? res : (res?.list || []) }))
            .catch(() => {})
            .finally(() => this.setState({ loading: false }))
    }

    loadApiList = () => {
        return getApiRegistryList({})
            .then(res => this.setState({ apiDataSource: Array.isArray(res) ? res : (res?.list || []) }))
            .catch(() => {})
    }

    loadServiceInfo = () => {
        const serviceId = this.getServiceId()
        if (!serviceId) return Promise.resolve()
        return getMcpServiceList({}).then(res => {
            const list = Array.isArray(res) ? res : (res?.list || [])
            const service = list.find(s => s.id === serviceId)
            if (service) this.setState({ serviceInfo: service })
        }).catch(() => {})
    }

    normalizeVersionList = (res) => {
        if (Array.isArray(res)) return res
        if (Array.isArray(res?.list)) return res.list
        if (Array.isArray(res?.records)) return res.records
        if (Array.isArray(res?.data)) return res.data
        if (Array.isArray(res?.data?.list)) return res.data.list
        if (Array.isArray(res?.data?.records)) return res.data.records
        return []
    }

    normalizeVersionStatus = (status) => {
        if (status === undefined || status === null) return ''
        const normalized = String(status).trim().toLowerCase()
        if (normalized === '2') return 'published'
        if (normalized === '4') return 'pending_publish'
        if (normalized === '3') return 'offline'
        if (normalized === '1') return 'pending_review'
        return normalized
    }

    getPreferredTestVersion = (versions = [], record) => {
        const normalizedVersions = (versions || []).filter(item => item && (item.id || item.versionId))
        if (!normalizedVersions.length) return null

        const latestVersion = normalizedVersions[0]
        const publishedVersion = normalizedVersions.find(item => this.normalizeVersionStatus(item.status) === 'published')
        return latestVersion || publishedVersion || null
    }

    getErrorMessage = (error, fallback) => {
        const rawMessage = error?.message
            || error?.msg
            || error?.response?.data?.message
            || error?.response?.data?.msg
            || error?.data?.message
            || error?.data?.msg

        if (rawMessage) return rawMessage

        try {
            const serialized = JSON.stringify(error)
            return serialized && serialized !== '{}' ? serialized : fallback
        } catch (e) {
            return fallback
        }
    }

    buildManagedDebugInvalidResult = (messageText = '接口配置已变更，请重新调试接口') => ({
        success: false,
        stale: true,
        message: messageText,
        description: '接口地址、请求头、参数定义或测试入参发生变化后，需要重新联调一次，避免发布旧响应结构。'
    })

    markManagedDebugDirty = (reason = '接口配置已变更，请重新调试接口') => {
        if (this.state.apiMode !== 'managed') return
        this.setState(prev => {
            const nextState = {
                managedDebugRequired: true,
                lastDebugSignature: null
            }
            if (prev.lastDebugSignature || prev.debugResult?.success) {
                nextState.debugResult = this.buildManagedDebugInvalidResult(reason)
            }
            return nextState
        })
    }

    normalizeJsonString = (rawValue) => {
        const text = (rawValue || '').trim()
        if (!text) return '{}'
        try {
            return JSON.stringify(JSON.parse(text))
        } catch (e) {
            return text
        }
    }

    escapeRegExp = (value = '') => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

    replacePathToken = (url = '', paramName = '', rawValue) => {
        const safeName = this.escapeRegExp(paramName)
        const encoded = encodeURIComponent(rawValue === undefined || rawValue === null ? '' : String(rawValue))
        return String(url || '')
            .replace(new RegExp(`\\{${safeName}\\}`, 'g'), encoded)
            .replace(new RegExp(`:${safeName}(?=/|$)`, 'g'), encoded)
            .replace(new RegExp(`\\[${safeName}\\]`, 'g'), encoded)
    }

    stringifyQueryValue = (value) => {
        if (value === undefined || value === null) return ''
        if (typeof value === 'string') return value
        return JSON.stringify(value)
    }

    appendQueryParams = (url = '', queryParams = {}) => {
        const entries = Object.entries(queryParams).filter(([, value]) => value !== undefined)
        if (!entries.length) return url

        try {
            const urlObj = new URL(url)
            entries.forEach(([key, value]) => {
                if (Array.isArray(value)) {
                    value.forEach(item => urlObj.searchParams.append(key, this.stringifyQueryValue(item)))
                    return
                }
                urlObj.searchParams.set(key, this.stringifyQueryValue(value))
            })
            return urlObj.toString()
        } catch (e) {
            const queryString = entries.map(([key, value]) => {
                if (Array.isArray(value)) {
                    return value.map(item => `${encodeURIComponent(key)}=${encodeURIComponent(this.stringifyQueryValue(item))}`).join('&')
                }
                return `${encodeURIComponent(key)}=${encodeURIComponent(this.stringifyQueryValue(value))}`
            }).filter(Boolean).join('&')
            if (!queryString) return url
            return `${url}${url.includes('?') ? '&' : '?'}${queryString}`
        }
    }

    parseDebugInputObject = (rawValue) => {
        const text = (rawValue || '').trim()
        if (!text) return {}
        const parsed = JSON.parse(text)
        if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') {
            throw new Error('测试入参 JSON 必须是对象')
        }
        return parsed
    }

    cloneJsonValue = (value) => {
        if (value === undefined) return undefined
        return JSON.parse(JSON.stringify(value))
    }

    computeManagedDebugSignature = (values = {}) => {
        const requestMeta = this.getRequestMetaFromValues(values)
        const sanitizedParams = this.sanitizeParams(this.state.parsedParams, requestMeta)
            .map(item => ({
                paramName: item.paramName,
                fullPath: item.fullPath,
                location: item.location,
                required: !!item.required,
                paramType: item.paramType
            }))
        const fixedHeaders = (this.state.fixedHeaders || [])
            .filter(item => (item?.headerName || '').trim())
            .map(item => ({
                headerName: (item.headerName || '').trim(),
                headerValue: item.headerValue || ''
            }))
        return JSON.stringify({
            method: this.normalizeRequestMethod(requestMeta.method),
            url: (requestMeta.url || '').trim(),
            params: sanitizedParams,
            fixedHeaders,
            debugInputJson: this.normalizeJsonString(values.debugInputJson)
        })
    }

    openCreate = (afterOpen) => {
        const serviceId = this.getServiceId()
        if (!serviceId) {
            message.warning('请先从「MCP服务管理」进入具体服务后再创建工具，否则工具无法归属到任何服务')
        }
        this.setState({
            drawerVisible: true,
            editRecord: null,
            parsedParams: [],
            parsedOutputs: [],
            inputSchemaEmptyObject: false,
            jsonSchemaImportVisible: false,
            jsonOutputImportVisible: false,
            selectedApiCode: undefined,
            apiMode: 'bind_existing',
            formSnapshot: { method: 'POST', category: '其他', apiMode: 'bind_existing', authRequired: false, userContext: false, maxRows: 100, debugInputJson: '{}' },
            fixedHeaders: [],
            responseMappings: [],
            examples: [],
            specPreviewData: null,
            debugging: false,
            debugResult: null,
            managedDebugRequired: true,
            lastDebugSignature: null
        })
        setTimeout(() => {
            if (this.formRef.current) {
                this.formRef.current.resetFields()
                this.formRef.current.setFieldsValue({
                    method: 'POST', category: '其他', apiMode: 'bind_existing', authRequired: false, userContext: false, maxRows: 100, debugInputJson: '{}'
                })
            }
            if (typeof afterOpen === 'function') {
                afterOpen()
            }
        }, 0)
    }

    openEdit = async (record) => {
        let apiDataSource = this.state.apiDataSource
        let preMatchedApi = apiDataSource.find(api => api.apiCode === record.apiCode)
        if (record.apiCode && !preMatchedApi) {
            try {
                const apiRes = await getApiRegistryList({})
                apiDataSource = Array.isArray(apiRes) ? apiRes : (apiRes?.list || [])
                preMatchedApi = apiDataSource.find(api => api.apiCode === record.apiCode)
                if (apiDataSource.length) {
                    this.setState({ apiDataSource })
                }
            } catch (e) {
                // ignore and fall back to current dataSource
            }
        }
        const initialApiMode = record.apiCode && !this.isManagedDraftApi(preMatchedApi) ? 'bind_existing' : 'managed'
        this.setState({
            drawerVisible: true,
            editRecord: record,
            parsedParams: [],
            parsedOutputs: [],
            inputSchemaEmptyObject: false,
            jsonSchemaImportVisible: false,
            jsonOutputImportVisible: false,
            selectedApiCode: record.apiCode || undefined,
            apiMode: initialApiMode,
            fixedHeaders: [],
            responseMappings: [],
            examples: [],
            specPreviewData: null,
            debugging: false,
            debugResult: null,
            managedDebugRequired: false,
            lastDebugSignature: null
        })

        // 我们尝试拉取最新的 Version，然后还原表单
        try {
            const versionRes = await getMcpToolVersionList({ toolId: record.id })
            const versions = this.normalizeVersionList(versionRes)
            const activeVersion = versions && versions.length > 0 ? versions[0] : null

            // 匹配 API 信息
            const matchedApi = apiDataSource.find(api => api.apiCode === record.apiCode)
            const resolvedApiMode = record.apiCode && !this.isManagedDraftApi(matchedApi) ? 'bind_existing' : 'managed'

            const parsedConfig = this.safeParseJson(activeVersion?.configJson, 'Failed to parse configJson in edit mode')
            const policy = activeVersion?.policyJson ? this.safeParseJson(activeVersion.policyJson) : (parsedConfig?.policy || null)
            const fixedHeaders = Array.isArray(policy?.fixedHeaders) ? policy.fixedHeaders : []
            const responseMapping = this.safeParseJson(activeVersion?.responseMapping) || parsedConfig?.binding?.responseMapping || {}
            const responseMappings = Object.keys(responseMapping || {}).map(key => ({ fieldName: key, jsonPath: responseMapping[key] }))
            const examples = Array.isArray(activeVersion?.examples) ? activeVersion.examples.map(item => ({
                exampleName: item?.exampleName || item?.name || '',
                userQuery: item?.userQuery || item?.query || '',
                inputJson: item?.inputJson || JSON.stringify(item?.input || {}, null, 2),
                outputJson: item?.outputJson || JSON.stringify(item?.output || {}, null, 2)
            })) : (Array.isArray(parsedConfig?.examples) ? parsedConfig.examples.map(item => ({
                exampleName: item?.name || '',
                userQuery: item?.query || '',
                inputJson: JSON.stringify(item?.input || {}, null, 2),
                outputJson: JSON.stringify(item?.output || {}, null, 2)
            })) : [])

            // 兼容不同接口返回结构：spec / configJson.tool / 顶层字段
            const specLike = activeVersion?.spec || parsedConfig || {}
            const specDescription = activeVersion?.spec?.description
                || parsedConfig?.tool?.description
                || activeVersion?.description
                || ''

            setTimeout(() => {
                if (this.formRef.current) {
                    const formValues = {
                        name: record.name || record.toolName || activeVersion?.spec?.name || parsedConfig?.tool?.name || activeVersion?.toolName || '',
                        toolCode: record.toolCode,
                        description: record.description || specDescription || '',
                        category: record.category || '其他',
                        apiMode: resolvedApiMode,
                        apiCode: resolvedApiMode === 'bind_existing' ? (record.apiCode || undefined) : undefined,
                        method: matchedApi?.method || record.method || 'POST',
                        url: matchedApi ? this.getApiUrl(matchedApi) : (record.url || ''),
                        authRequired: !!policy?.authRequired,
                        userContext: !!policy?.userContext,
                        maxRows: policy?.maxRows || 100,
                        debugInputJson: examples[0]?.inputJson || '{}'
                    }
                    this.formRef.current.setFieldsValue(formValues)
                    this.setState({
                        formSnapshot: formValues,
                        fixedHeaders,
                        responseMappings,
                        examples,
                        apiMode: resolvedApiMode,
                        selectedApiCode: record.apiCode || undefined
                    })
                }
            }, 0)

            const stateUpdates = {}

            const versionParams = Array.isArray(activeVersion?.params) ? activeVersion.params : []
            const versionOutputs = Array.isArray(activeVersion?.outputs) ? activeVersion.outputs : []
            const inputSchema = specLike?.inputSchema
            const outputSchema = specLike?.outputSchema

            const parsedInputs = this.parseParamDefinitions({
                schema: inputSchema,
                items: versionParams,
                fieldNameKey: 'paramName',
                requestMeta: {
                    method: matchedApi?.method || record.method,
                    url: matchedApi ? this.getApiUrl(matchedApi) : (record.url || '')
                }
            })
            const parsedOutputs = this.parseParamDefinitions({
                schema: outputSchema,
                items: versionOutputs,
                fieldNameKey: 'fieldName',
                unwrapResponseEnvelope: true
            })

            stateUpdates.parsedParams = parsedInputs.fields
            stateUpdates.parsedOutputs = parsedOutputs.fields
            stateUpdates.inputSchemaEmptyObject = parsedInputs.isEmptyObjectSchema && parsedInputs.fields.length === 0

            if (Object.keys(stateUpdates).length > 0) {
                this.setState(stateUpdates)
            }
        } catch (error) {
            console.error(error)
        }
    }

    handleApiSelect = (apiCode) => {
        if (!apiCode) {
            this.setState({
                selectedApiCode: undefined,
                apiMode: 'managed',
                managedDebugRequired: true,
                debugResult: null,
                lastDebugSignature: null
            })
            return
        }
        const { apiDataSource } = this.state
        const api = apiDataSource.find(a => a.apiCode === apiCode)
        if (!api) return
        if (this.isManagedDraftApi(api)) {
            message.warning('自动托管接口不能作为“复用已有接口”来源，请切换到“新建接口并托管”模式编辑')
            return
        }
        this.setState({
            selectedApiCode: apiCode,
            apiMode: 'bind_existing',
            debugResult: null,
            managedDebugRequired: false,
            lastDebugSignature: null
        })

        const parsedInputs = this.parseParamDefinitions({
            schema: api.requestSchema,
            fieldNameKey: 'paramName',
            defaultDescription: 'API带入参数',
            requestMeta: {
                method: api.method,
                url: this.getApiUrl(api)
            }
        })
        const parsedOutputs = this.parseParamDefinitions({
            schema: api.responseSchema,
            fieldNameKey: 'fieldName',
            unwrapResponseEnvelope: true,
            defaultDescription: 'API返回字段'
        })

        const newParams = parsedInputs.fields
        const newOutputs = parsedOutputs.fields
        const jsonExampleObj = parsedInputs.exampleObject
        const jsonOutputObj = parsedOutputs.exampleObject
        const requestSchemaIsExplicitEmptyObject = parsedInputs.isEmptyObjectSchema

        const currentDesc = this.formRef.current?.getFieldValue('description') || ''
        const newDesc = api.description ? (currentDesc ? `${currentDesc}\n${api.description}` : api.description) : currentDesc

        let updates = {
            apiCode: api.apiCode,
            method: api.method || 'POST',
            url: this.getApiUrl(api),
            description: newDesc,
        }

        const currentName = this.formRef.current?.getFieldValue('name')
        const currentToolCode = this.formRef.current?.getFieldValue('toolCode')
        if (!currentName) updates.name = api.apiName || ''
        if (!currentToolCode) updates.toolCode = api.apiCode || ''

        if (Object.keys(jsonExampleObj).length > 0) {
            updates.jsonExample = JSON.stringify(jsonExampleObj, null, 2)
        } else if (requestSchemaIsExplicitEmptyObject) {
            updates.jsonExample = JSON.stringify({}, null, 2)
        }
        if (Object.keys(jsonOutputObj).length > 0) {
            updates.jsonOutputExample = JSON.stringify(jsonOutputObj, null, 2)
        }

        if (this.formRef.current) {
            this.formRef.current.setFieldsValue(updates)
        }
        this.setState(prev => ({ formSnapshot: { ...prev.formSnapshot, ...updates } }))

        const stateUpdates = {}
        if (newParams.length > 0) stateUpdates.parsedParams = newParams
        if (newOutputs.length > 0) stateUpdates.parsedOutputs = newOutputs
        stateUpdates.inputSchemaEmptyObject = requestSchemaIsExplicitEmptyObject && newParams.length === 0
        if (Object.keys(stateUpdates).length > 0) {
            this.setState(stateUpdates)
        }

        if (newParams.length > 0 || newOutputs.length > 0) {
            message.success('已自动带入接口配置及参数模板')
        } else if (requestSchemaIsExplicitEmptyObject) {
            message.success('已自动带入接口配置，该接口无需输入参数')
        } else {
            message.success('已自动带入接口配置')
        }
    }

    handleApiModeChange = e => {
        const apiMode = typeof e === 'string' ? e : e?.target?.value
        if (!apiMode) return

        const nextSelectedApiCode = apiMode === 'bind_existing' ? this.state.selectedApiCode : undefined
        this.setState({
            apiMode,
            selectedApiCode: nextSelectedApiCode,
            inputSchemaEmptyObject: apiMode === 'bind_existing' ? this.state.inputSchemaEmptyObject : false,
            debugResult: null,
            managedDebugRequired: apiMode === 'managed',
            lastDebugSignature: null
        })
        if (!this.formRef.current) return
        this.formRef.current.setFieldsValue({
            apiMode,
            apiCode: nextSelectedApiCode,
            jsonExample: apiMode === 'bind_existing' ? this.formRef.current.getFieldValue('jsonExample') : undefined,
            debugInputJson: apiMode === 'managed' ? (this.formRef.current.getFieldValue('debugInputJson') || '{}') : undefined
        })
    }

    safeParseJson = (rawValue, errorLabel) => {
        if (!rawValue) return null
        if (typeof rawValue === 'object') return rawValue
        try {
            return JSON.parse(rawValue)
        } catch (e) {
            if (errorLabel) {
                console.error(errorLabel, e)
            }
            return null
        }
    }

    getApiUrl = (api = {}) => `${api?.baseUrl || ''}${api?.path || ''}`

    normalizeRequestMethod = (method) => String(method || 'POST').toUpperCase()

    getRequestMetaFromValues = (values = {}, selectedApi) => {
        if ((values.apiMode || 'managed') === 'bind_existing' && selectedApi) {
            return {
                method: selectedApi.method || values.method,
                url: this.getApiUrl(selectedApi) || values.url || ''
            }
        }

        return {
            method: values.method,
            url: values.url
        }
    }

    getCurrentRequestMeta = () => {
        const values = this.formRef.current?.getFieldsValue?.(['method', 'url']) || {}
        return {
            method: values.method,
            url: values.url
        }
    }

    buildManagedDebugPayload = (values = {}, selectedApi) => {
        const requestMeta = this.getRequestMetaFromValues(values, selectedApi)
        const method = this.normalizeRequestMethod(requestMeta.method || values.method)
        const originalUrl = (requestMeta.url || values.url || '').trim()
        if (!originalUrl) throw new Error('请先填写完整 API URL')

        const inputObject = this.parseDebugInputObject(values.debugInputJson)
        const sanitizedParams = this.sanitizeParams(this.state.parsedParams, requestMeta)
        const fixedHeaders = (this.state.fixedHeaders || [])
            .filter(item => (item?.headerName || '').trim())
            .reduce((acc, item) => {
                acc[(item.headerName || '').trim()] = item.headerValue || ''
                return acc
            }, {})

        let resolvedUrl = originalUrl
        const headerParams = {}
        const queryParams = {}
        const bodyPayload = this.cloneJsonValue(inputObject)
        const isBodyMethod = !['GET', 'DELETE', 'HEAD'].includes(method)

        sanitizedParams.forEach(item => {
            const rootName = this.getRootParamName(item.fullPath || item.paramName)
            if (!rootName || !Object.prototype.hasOwnProperty.call(inputObject, rootName)) return
            const value = inputObject[rootName]
            switch (item.location) {
                case 'path':
                    if (value === undefined || value === null || value === '') {
                        throw new Error(`路径参数 ${rootName} 不能为空`)
                    }
                    resolvedUrl = this.replacePathToken(resolvedUrl, rootName, value)
                    delete bodyPayload[rootName]
                    break
                case 'header':
                    headerParams[rootName] = value
                    delete bodyPayload[rootName]
                    break
                case 'query':
                    queryParams[rootName] = value
                    delete bodyPayload[rootName]
                    break
                default:
                    break
            }
        })

        const headers = { ...fixedHeaders }
        Object.keys(headerParams).forEach(key => {
            headers[key] = headerParams[key] === undefined || headerParams[key] === null
                ? ''
                : String(headerParams[key])
        })

        if (!isBodyMethod && bodyPayload && typeof bodyPayload === 'object') {
            Object.keys(bodyPayload).forEach(key => {
                if (!Object.prototype.hasOwnProperty.call(queryParams, key)) {
                    queryParams[key] = bodyPayload[key]
                }
            })
        }

        const finalUrl = this.appendQueryParams(resolvedUrl, queryParams)
        let body = null
        if (isBodyMethod) {
            body = JSON.stringify(bodyPayload || {})
            const hasContentType = Object.keys(headers).some(key => key.toLowerCase() === 'content-type')
            if (!hasContentType) {
                headers['Content-Type'] = 'application/json'
            }
        }

        return {
            method,
            url: finalUrl,
            headers: Object.keys(headers).map(key => ({
                headerName: key,
                headerValue: headers[key]
            })),
            body
        }
    }

    extractPathParamNames = (urlOrPath = '') => {
        let targetPath = String(urlOrPath || '')
        if (!targetPath) return new Set()

        try {
            const urlObj = new URL(targetPath)
            targetPath = urlObj.pathname
        } catch (e) {
            targetPath = targetPath.split('?')[0].split('#')[0]
        }

        const names = new Set()
        const patterns = [/\{([^}]+)\}/g, /:([A-Za-z0-9_]+)/g, /\[([^[\]/]+)\]/g]

        patterns.forEach((pattern) => {
            let match = pattern.exec(targetPath)
            while (match) {
                const name = (match[1] || '').trim()
                if (name) names.add(name)
                match = pattern.exec(targetPath)
            }
        })

        return names
    }

    normalizeParamPath = (path = '') => String(path || '').trim().replace(/\[\]/g, '')

    getRootParamName = (path = '') => {
        const normalizedPath = this.normalizeParamPath(path)
        return normalizedPath.split('.')[0] || ''
    }

    isTopLevelParamPath = (path = '') => {
        const normalizedPath = this.normalizeParamPath(path)
        return !!normalizedPath && !normalizedPath.includes('.') && normalizedPath !== '[item]'
    }

    inferParamLocation = ({ paramName = '', fullPath = '', method, url, inheritedLocation } = {}) => {
        if (inheritedLocation) return inheritedLocation

        const effectivePath = (fullPath || paramName || '').trim()
        const rootParamName = this.getRootParamName(effectivePath)
        const pathParamNames = this.extractPathParamNames(url)

        if (this.isTopLevelParamPath(effectivePath) && rootParamName && pathParamNames.has(rootParamName)) {
            return 'path'
        }

        return ['GET', 'DELETE', 'HEAD'].includes(this.normalizeRequestMethod(method)) ? 'query' : 'body'
    }

    resolveParamLocation = ({ item = {}, fieldNameKey = 'paramName', requestMeta = {}, inheritedLocation } = {}) => {
        if (item?.location) return item.location

        const fullPath = item?.fullPath || item?.[fieldNameKey] || item?.paramName || item?.fieldName || item?.name || ''
        const paramName = item?.[fieldNameKey] || item?.paramName || item?.fieldName || item?.name || ''

        return this.inferParamLocation({
            paramName,
            fullPath,
            method: requestMeta.method,
            url: requestMeta.url,
            inheritedLocation
        })
    }

    refreshInferredParamLocations = (
        nodes = [],
        prevRequestMeta = {},
        nextRequestMeta = {},
        currentParentLocation,
        nextParentLocation
    ) => nodes.map(node => {
        const fieldName = node?.paramName || node?.fieldName || ''
        const fullPath = node?.fullPath || fieldName
        const previousDefaultLocation = this.inferParamLocation({
            paramName: fieldName,
            fullPath,
            method: prevRequestMeta.method,
            url: prevRequestMeta.url,
            inheritedLocation: currentParentLocation
        })
        const nextDefaultLocation = this.inferParamLocation({
            paramName: fieldName,
            fullPath,
            method: nextRequestMeta.method,
            url: nextRequestMeta.url,
            inheritedLocation: nextParentLocation
        })
        const shouldRefreshLocation = !node?.location || node.location === previousDefaultLocation
        const location = shouldRefreshLocation ? nextDefaultLocation : node.location

        return {
            ...node,
            location,
            children: Array.isArray(node.children)
                ? this.refreshInferredParamLocations(
                    node.children,
                    prevRequestMeta,
                    nextRequestMeta,
                    node.location || previousDefaultLocation,
                    location
                )
                : node.children
        }
    })

    handleRequestMetaFieldChange = (field, value) => {
        const prevRequestMeta = this.getCurrentRequestMeta()
        const nextRequestMeta = {
            ...prevRequestMeta,
            [field]: value
        }

        this.setState(prev => ({
            parsedParams: this.refreshInferredParamLocations(prev.parsedParams, prevRequestMeta, nextRequestMeta)
        }))
        this.markManagedDebugDirty('接口地址或请求方式已变更，请重新调试接口')
    }

    buildFieldsFromSchema = (schema, fieldNameKey, unwrapResponseEnvelope = false, defaultDescription = '', requestMeta = {}) => {
        return this.buildTreeFieldsFromSchema(schema, fieldNameKey, unwrapResponseEnvelope, defaultDescription, requestMeta)
    }

    buildJsonExampleFromSchema = (schema, unwrapResponseEnvelope = false) => {
        if (!schema) return {}
        const props = this.extractSchemaProperties(schema, unwrapResponseEnvelope)
        if (!props) return {}
        return Object.keys(props).reduce((acc, key) => {
            const fieldSchema = props[key] || {}
            const type = fieldSchema?.type || 'string'
            acc[key] = this.getPlaceholderByType(type, fieldSchema)
            return acc
        }, {})
    }

    isExplicitEmptyObjectSchema = (schema) => {
        if (!schema || typeof schema !== 'object') return false
        if (schema.type === 'object' && (!schema.properties || Object.keys(schema.properties).length === 0)) {
            return true
        }
        return Object.keys(schema).length === 0
    }

    parseParamDefinitions = ({ schema, items = [], fieldNameKey, unwrapResponseEnvelope = false, defaultDescription = '', requestMeta = {} }) => {
        const normalizedItems = Array.isArray(items) ? items : []
        if (normalizedItems.length > 0) {
            const parsedItems = normalizedItems.map(item => ({
                key: item?.key || this.generateId('param'),
                [fieldNameKey]: item?.[fieldNameKey] || item?.name || '',
                paramType: item?.type || item?.paramType || 'string',
                description: item?.description || defaultDescription,
                required: !!item?.required,
                fullPath: item?.fullPath || item?.[fieldNameKey] || item?.name || '',
                location: this.resolveParamLocation({ item, fieldNameKey, requestMeta })
            })).filter(item => item[fieldNameKey])
            return {
                fields: parsedItems,
                exampleObject: {},
                isEmptyObjectSchema: false
            }
        }

        const normalizedSchema = this.safeParseJson(schema)
        if (!normalizedSchema) {
            return {
                fields: [],
                exampleObject: {},
                isEmptyObjectSchema: false
            }
        }

        return {
            fields: this.buildFieldsFromSchema(normalizedSchema, fieldNameKey, unwrapResponseEnvelope, defaultDescription, requestMeta),
            exampleObject: this.buildJsonExampleFromSchema(normalizedSchema, unwrapResponseEnvelope),
            isEmptyObjectSchema: this.isExplicitEmptyObjectSchema(normalizedSchema)
        }
    }

    extractSchemaProperties = (schema, unwrapResponseEnvelope = false) => {
        if (!schema || typeof schema !== 'object') return null
        if (unwrapResponseEnvelope && schema.properties) {
            const envelopeKey = ['value', 'data', 'result'].find(key => schema.properties[key])
            if (envelopeKey) {
                const node = schema.properties[envelopeKey] || {}
                if (node.properties && typeof node.properties === 'object') {
                    return node.properties
                }
                if (node.type === 'array' && node.items && typeof node.items.properties === 'object') {
                    return node.items.properties
                }
            }
        }
        if (schema.properties && typeof schema.properties === 'object') {
            return schema.properties
        }
        if (schema.type !== 'object' && Object.keys(schema).length > 0) {
            const fallback = {}
            Object.keys(schema).forEach(k => {
                fallback[k] = { type: typeof schema[k] }
            })
            return fallback
        }
        return null
    }
//新增一个类型归一函数
    normalizeSchemaType = (schemaNode = {}) => {
        const rawType = schemaNode?.type

        if (Array.isArray(rawType)) {
            const valid = rawType.filter(Boolean).filter(t => t !== 'null')
            if (valid.includes('array')) return 'array'
            if (valid.includes('object')) return 'object'
            if (valid.includes('integer')) return 'integer'
            if (valid.includes('number')) return 'number'
            if (valid.includes('boolean')) return 'boolean'
            return valid[0] || 'string'
        }

        if (rawType) return rawType
        if (schemaNode?.properties) return 'object'
        if (schemaNode?.items) return 'array'
        return 'string'
    }
//新增“递归解析 schema 为树”的函数
    buildTreeFieldsFromSchema = (
        schema,
        fieldNameKey = 'paramName',
        unwrapResponseEnvelope = false,
        defaultDescription = '',
        requestMeta = {}
    ) => {
        if (!schema || typeof schema !== 'object') return []

        const rootSchema = unwrapResponseEnvelope
            ? this.extractResponseEnvelopeSchema(schema) || schema
            : schema

        const visit = (node, options = {}) => {
            const {
                name = '',
                fullPath = '',
                parentRequiredSet = new Set(),
                location,
                level = 0
            } = options

            if (!node || typeof node !== 'object') return null

            const nodeType = this.normalizeSchemaType(node)
            const currentKey = this.generateId(fullPath || name || 'root')
            const resolvedLocation = this.inferParamLocation({
                paramName: name,
                fullPath,
                method: requestMeta.method,
                url: requestMeta.url,
                inheritedLocation: location
            })

            const current = name ? {
                key: currentKey,
                [fieldNameKey]: name,
                fullPath,
                paramType: nodeType,
                description: node?.description || defaultDescription,
                required: parentRequiredSet.has(name),
                location: resolvedLocation,
                children: []
            } : null

            // object -> 继续解析 properties
            if (nodeType === 'object' && node.properties && typeof node.properties === 'object') {
                const requiredSet = new Set(Array.isArray(node.required) ? node.required : [])
                const children = Object.keys(node.properties).map(childName => {
                    const childNode = node.properties[childName]
                    const childPath = fullPath ? `${fullPath}.${childName}` : childName
                    return visit(childNode, {
                        name: childName,
                        fullPath: childPath,
                        parentRequiredSet: requiredSet,
                        location: resolvedLocation,
                        level: level + 1
                    })
                }).filter(Boolean)

                if (current) {
                    current.children = children
                    return current
                }
                return children
            }

            // array -> 如果 items 是 object/array，继续往下挂 children
            if (nodeType === 'array') {
                const itemsNode = node.items || {}
                const itemsType = this.normalizeSchemaType(itemsNode)

                if (itemsType === 'object' && itemsNode.properties) {
                    const requiredSet = new Set(Array.isArray(itemsNode.required) ? itemsNode.required : [])
                    const children = Object.keys(itemsNode.properties).map(childName => {
                        const childSchema = itemsNode.properties[childName]
                        const childPath = fullPath ? `${fullPath}[].${childName}` : `${childName}`
                        return visit(childSchema, {
                            name: childName,
                            fullPath: childPath,
                            parentRequiredSet: requiredSet,
                            location: resolvedLocation,
                            level: level + 1
                        })
                    }).filter(Boolean)

                    if (current) {
                        current.children = children
                        return current
                    }
                }

                if (itemsType === 'array') {
                    const arrayChild = visit(itemsNode, {
                        name: '[item]',
                        fullPath: fullPath ? `${fullPath}[]` : '[item]',
                        parentRequiredSet: new Set(),
                        location: resolvedLocation,
                        level: level + 1
                    })
                    if (current && arrayChild) {
                        current.children = [arrayChild]
                    }
                }

                return current
            }

            return current
        }

        if (rootSchema.type !== 'object' && !rootSchema.properties) return []

        const rootRequiredSet = new Set(Array.isArray(rootSchema.required) ? rootSchema.required : [])
        return Object.keys(rootSchema.properties || {}).map(name => {
            const childNode = rootSchema.properties[name]
            return visit(childNode, {
                name,
                fullPath: name,
                parentRequiredSet: rootRequiredSet,
                level: 0
            })
        }).filter(Boolean)
    }
    //新增响应 envelope 提取函数
extractResponseEnvelopeSchema = (schema) => {
    if (!schema || typeof schema !== 'object' || !schema.properties) return null

    const envelopeKey = ['value', 'data', 'result'].find(key => schema.properties[key])
    if (!envelopeKey) return null

    return schema.properties[envelopeKey] || null
}
    getPlaceholderByType = (type, rawValue) => {
        if (Array.isArray(rawValue)) return []
        if (type === 'string') return ''
        if (type === 'number' || type === 'integer') return 0
        if (type === 'boolean') return false
        if (type === 'array') return []
        return {}
    }

    hasExplicitEmptyInputSchema = (selectedApi, jsonExample) => {
        if (jsonExample) {
            try {
                const parsed = typeof jsonExample === 'string' ? JSON.parse(jsonExample) : jsonExample
                if (parsed && parsed.type === 'object' && (!parsed.properties || Object.keys(parsed.properties).length === 0)) {
                    return true
                }
                if (parsed && Object.keys(parsed).length === 0) {
                    return true
                }
            } catch (e) {
                // ignore invalid json here; form validation is handled elsewhere
            }
        }
        if (!selectedApi?.requestSchema) {
            return false
        }
        try {
            const parsed = typeof selectedApi.requestSchema === 'string'
                ? JSON.parse(selectedApi.requestSchema)
                : selectedApi.requestSchema
            if (!parsed) return false
            if (parsed.type === 'object' && (!parsed.properties || Object.keys(parsed.properties).length === 0)) {
                return true
            }
            return Object.keys(parsed).length === 0
        } catch (e) {
            return false
        }
    }

    sanitizeParams = (params = [], requestMeta = {}) => {
        const flatParams = this.flattenTreeParams(params)

        return flatParams
            .map(item => ({
                paramName: (item?.paramName || '').trim(),
                paramType: item?.paramType || 'string',
                description: (item?.description || '').trim(),
                required: !!item?.required,
                fullPath: item?.fullPath || item?.paramName || '',
                location: this.resolveParamLocation({ item, requestMeta })
            }))
            .filter(item => item.paramName)
    }

    sanitizeOutputs = (outputs = []) => outputs
        .map(item => ({
            fieldName: (item?.fieldName || '').trim(),
            paramType: item?.paramType || 'string',
            description: (item?.description || '').trim()
        }))
        .filter(item => item.fieldName)

    buildResponseMappingsFromOutputs = (outputs = []) => this.sanitizeOutputs(outputs).map(item => ({
        fieldName: item.fieldName,
        jsonPath: `$.${item.fieldName}`
    }))

    areResponseMappingsEqual = (left = [], right = []) => {
        const normalize = (mappings = []) => mappings.map(item => ({
            fieldName: (item?.fieldName || '').trim(),
            jsonPath: (item?.jsonPath || '').trim()
        })).filter(item => item.fieldName || item.jsonPath)

        return JSON.stringify(normalize(left)) === JSON.stringify(normalize(right))
    }

    autoParseJson = () => {
        const raw = this.formRef.current?.getFieldValue('jsonExample')

        if (!raw || !String(raw).trim()) {
            message.warning('请先贴入 JSON Schema')
            return
        }

        const schema = this.safeParseJson(raw, 'Failed to parse input JSON Schema')
        if (!schema) {
            message.error('JSON Schema 格式不合法')
            return
        }

        if (schema.type !== 'object' && !schema.properties) {
            message.error('仅支持 object 类型的 JSON Schema')
            return
        }

        const requestMeta = this.getCurrentRequestMeta()
        const params = this.buildTreeFieldsFromSchema(schema, 'paramName', false, '自动解析参数', requestMeta)

        this.setState({
            parsedParams: params,
            inputSchemaEmptyObject: this.isExplicitEmptyObjectSchema(schema)
        })
        this.markManagedDebugDirty('输入参数定义已变更，请重新调试接口')

        message.success(params.length ? '已根据 JSON Schema 生成树形参数表' : '该 Schema 无输入参数')
    }
//新增树形数据的更新函数

updateTreeNode = (nodes = [], targetKey, patch) => {
    return nodes.map(node => {
        if (node.key === targetKey) {
            const nextPatch = typeof patch === 'function' ? patch(node) : patch
            return { ...node, ...nextPatch }
        }
        if (Array.isArray(node.children) && node.children.length > 0) {
            return {
                ...node,
                children: this.updateTreeNode(node.children, targetKey, patch)
            }
        }
        return node
    })
}
//删除节点
removeTreeNode = (nodes = [], targetKey) => {
    return nodes
        .filter(node => node.key !== targetKey)
        .map(node => ({
            ...node,
            children: Array.isArray(node.children)
                ? this.removeTreeNode(node.children, targetKey)
                : []
        }))
}
    handleParamChange = (rowKey, field, value) => {
    const requestMeta = this.getCurrentRequestMeta()
    this.setState(prev => ({
        parsedParams: this.updateTreeNode(prev.parsedParams, rowKey, (node) => {
            const nextPatch = { [field]: value }

            if (field === 'paramName') {
                const previousDefaultLocation = this.resolveParamLocation({ item: node, requestMeta })
                const nextDefaultLocation = this.resolveParamLocation({
                    item: {
                        ...node,
                        paramName: value,
                        fullPath: node.fullPath || value
                    },
                    requestMeta
                })

                if (!node.location || node.location === previousDefaultLocation) {
                    nextPatch.location = nextDefaultLocation
                }
            }

            return nextPatch
        })
    }))
    this.markManagedDebugDirty('输入参数已变更，请重新调试接口')
}

    removeParam = (rowKey) => {
    this.setState(prev => ({
        parsedParams: this.removeTreeNode(prev.parsedParams, rowKey)
    }))
    this.markManagedDebugDirty('输入参数已变更，请重新调试接口')
}
//新增 flatten
flattenTreeParams = (nodes = []) => {
    const result = []

    const walk = (items) => {
        items.forEach(item => {
            result.push({
                paramName: item.fullPath || item.paramName || '',
                fullPath: item.fullPath || item.paramName || '',
                paramType: item.paramType || 'string',
                description: item.description || '',
                required: !!item.required,
                location: item.location
            })
            if (Array.isArray(item.children) && item.children.length > 0) {
                walk(item.children)
            }
        })
    }

    walk(nodes)
    return result
}

    addEmptyParam = () => {
        const { parsedParams } = this.state
        const newKey = this.generateId('param')
        this.setState({
            parsedParams: [
                ...parsedParams,
                {
                    key: newKey,
                    paramName: '',
                    paramType: 'string',
                    description: '',
                    required: false,
                    location: this.inferParamLocation(this.getCurrentRequestMeta())
                }
            ]
        })
        this.markManagedDebugDirty('输入参数已变更，请重新调试接口')
    }

    handleOutputChange = (rowKey, field, value) => {
        const { parsedOutputs } = this.state
        const newOutputs = parsedOutputs.map((item) => {
            if (item.key === rowKey) {
                return { ...item, [field]: value }
            }
            return item
        })
        this.setState({ parsedOutputs: newOutputs })
    }

    removeOutput = (rowKey) => {
        const { parsedOutputs } = this.state
        const newOutputs = parsedOutputs.filter(item => item.key !== rowKey)
        this.setState({ parsedOutputs: newOutputs })
    }

    addEmptyOutput = () => {
        const { parsedOutputs } = this.state
        this.setState({
            parsedOutputs: [
                ...parsedOutputs,
                {
                    key: this.generateId('output'),
                    fieldName: '',
                    paramType: 'string',
                    description: ''
                }
            ]
        })
    }

    handleFixedHeaderChange = (index, field, value) => {
        const fixedHeaders = this.state.fixedHeaders.map((item, idx) => {
            if (idx === index) {
                return { ...item, [field]: value }
            }
            return item
        })
        this.setState({ fixedHeaders })
        this.markManagedDebugDirty('请求头已变更，请重新调试接口')
    }

    addFixedHeader = () => {
        this.setState({ fixedHeaders: [...this.state.fixedHeaders, { headerName: '', headerValue: '' }] })
        this.markManagedDebugDirty('请求头已变更，请重新调试接口')
    }

    removeFixedHeader = (index) => {
        const fixedHeaders = [...this.state.fixedHeaders]
        fixedHeaders.splice(index, 1)
        this.setState({ fixedHeaders })
        this.markManagedDebugDirty('请求头已变更，请重新调试接口')
    }

    shouldShowFixedHeaders = () => {
        return (this.state.fixedHeaders || []).length > 0
    }

    normalizeOutputSource = (raw) => {
        if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
            if (raw.value && typeof raw.value === 'object' && !Array.isArray(raw.value)) return raw.value
            if (raw.data && typeof raw.data === 'object' && !Array.isArray(raw.data)) return raw.data
            if (raw.result && typeof raw.result === 'object' && !Array.isArray(raw.result)) return raw.result
            return raw
        }
        if (Array.isArray(raw)) {
            return { items: raw }
        }
        return { value: raw }
    }

    parseOutputFieldsFromResponse = (raw) => {
        const obj = this.normalizeOutputSource(raw)
        const mapType = (val) => {
            if (Array.isArray(val)) return 'array'
            if (val === null) return 'string'
            if (typeof val === 'number') return Number.isInteger(val) ? 'integer' : 'number'
            if (typeof val === 'object') return 'object'
            return typeof val
        }
        return Object.keys(obj || {}).map(key => ({
            key: this.generateId('output'),
            fieldName: key,
            paramType: mapType(obj[key]),
            description: '自动解析字段'
        }))
    }

    compareResponseWithSchema = (responseBody, schema) => {
        const actualFields = this.parseOutputFieldsFromResponse(responseBody).map(item => item.fieldName).filter(Boolean)
        const expectedFields = this.parseParamDefinitions({
            schema,
            fieldNameKey: 'fieldName',
            unwrapResponseEnvelope: true
        }).fields.map(item => item.fieldName).filter(Boolean)

        if (!expectedFields.length) {
            return {
                matched: true,
                summary: '接口中心未配置输出 schema，暂不做字段比对',
                missingFields: [],
                extraFields: []
            }
        }

        const missingFields = expectedFields.filter(field => !actualFields.includes(field))
        const extraFields = actualFields.filter(field => !expectedFields.includes(field))
        const matched = missingFields.length === 0

        return {
            matched,
            summary: matched
                ? '返回结构与接口 schema 一致'
                : `返回结构与接口 schema 不完全一致，缺少 ${missingFields.length} 个字段`,
            missingFields,
            extraFields
        }
    }

    upsertManagedDebugExample = (examples = [], inputJson = '{}', outputJson = '{}') => {
        const autoExample = {
            exampleName: '联调验证',
            userQuery: '',
            inputJson,
            outputJson
        }
        if (!examples.length) return [autoExample]
        const nextExamples = [...examples]
        const index = nextExamples.findIndex(item =>
            (item?.exampleName || '') === '联调验证'
            || !((item?.exampleName || '').trim() || (item?.userQuery || '').trim() || (item?.inputJson || '').trim() || (item?.outputJson || '').trim())
        )
        if (index >= 0) {
            nextExamples[index] = { ...nextExamples[index], ...autoExample }
        }
        return nextExamples
    }

    handleExampleChange = (index, field, value) => {
        const examples = this.state.examples.map((item, idx) => {
            if (idx === index) {
                return { ...item, [field]: value }
            }
            return item
        })
        this.setState({ examples })
    }

    addExample = () => {
        this.setState({ examples: [...this.state.examples, { exampleName: '', userQuery: '', inputJson: '', outputJson: '' }] })
    }

    removeExample = (index) => {
        const examples = [...this.state.examples]
        examples.splice(index, 1)
        this.setState({ examples })
    }

    executeToolDebug = async ({ debugInputJson, source = 'modal' } = {}) => {
        const values = this.formRef.current?.getFieldsValue?.() || {}
        const currentApiMode = values.apiMode || this.state.apiMode
        const selectedApi = currentApiMode === 'bind_existing'
            ? this.getApiByCode(values.apiCode || this.state.selectedApiCode)
            : null
        const currentToolId = this.state.editRecord?.id

        if (currentApiMode === 'managed') {
            try {
                await this.formRef.current?.validateFields?.(['method', 'url'])
            } catch (e) {
                return
            }
        } else if (!selectedApi) {
            message.warning('请先选择一个已有接口，再进行调试')
            return
        }

        const nextValues = {
            ...values,
            debugInputJson
        }

        let payload
        try {
            payload = this.buildManagedDebugPayload(nextValues, selectedApi)
        } catch (error) {
            const failedResult = {
                success: false,
                message: this.getErrorMessage(error, '调试请求组装失败')
            }
            this.setState({
                debugResult: currentApiMode === 'managed' ? failedResult : this.state.debugResult,
                toolDebugRunResult: failedResult,
                managedDebugRequired: currentApiMode === 'managed',
                lastDebugSignature: currentApiMode === 'managed' ? null : this.state.lastDebugSignature
            })
            return
        }

        this.setState({ debugging: true, toolDebugRunResult: null, ...(currentApiMode === 'managed' ? { debugResult: null } : {}) })

        try {
            const result = await debugManagedMcpToolRequest(payload)
            if (!result?.success) {
                const failedResult = {
                    success: false,
                    message: result?.error || '接口调试失败',
                    description: result?.errorDetail || (result?.statusCode ? `HTTP ${result.statusCode}` : undefined),
                    data: result?.responseBody
                }
                this.setState({
                    debugging: false,
                    toolDebugRunResult: failedResult,
                    toolDebugStatusByToolId: currentToolId ? {
                        ...this.state.toolDebugStatusByToolId,
                        [currentToolId]: 'failed'
                    } : this.state.toolDebugStatusByToolId,
                    toolDebugTimeByToolId: currentToolId ? {
                        ...this.state.toolDebugTimeByToolId,
                        [currentToolId]: new Date().toISOString()
                    } : this.state.toolDebugTimeByToolId,
                    ...(currentApiMode === 'managed'
                        ? { managedDebugRequired: true, lastDebugSignature: null, debugResult: failedResult }
                        : {})
                }, () => this.persistToolDebugCache(this.state.toolDebugStatusByToolId, this.state.toolDebugTimeByToolId))
                return
            }

            const responseBody = result?.responseBody
            const outputJsonText = JSON.stringify(responseBody, null, 2)
            const schemaCompare = currentApiMode === 'bind_existing' && selectedApi?.responseSchema
                ? this.compareResponseWithSchema(responseBody, selectedApi.responseSchema)
                : null
            const successResult = {
                success: true,
                message: currentApiMode === 'managed' ? '接口调用成功，已自动提取输出字段' : '接口调用成功',
                description: [
                    result?.statusCode ? `HTTP ${result.statusCode}` : '联调通过',
                    schemaCompare?.summary
                ].filter(Boolean).join(' · '),
                data: responseBody,
                schemaCompare
            }

            if (currentApiMode === 'managed') {
                const outputs = this.parseOutputFieldsFromResponse(responseBody)
                if (!outputs.length) {
                    const emptyResult = {
                        success: false,
                        message: '接口调用成功，但无法自动提取输出字段',
                        description: '请检查返回结构是否为标准 JSON 对象，或手工补充输出字段。',
                        data: responseBody
                    }
                    this.setState({
                        debugging: false,
                        toolDebugRunResult: emptyResult,
                        toolDebugStatusByToolId: currentToolId ? {
                            ...this.state.toolDebugStatusByToolId,
                            [currentToolId]: 'failed'
                        } : this.state.toolDebugStatusByToolId,
                        toolDebugTimeByToolId: currentToolId ? {
                            ...this.state.toolDebugTimeByToolId,
                            [currentToolId]: new Date().toISOString()
                        } : this.state.toolDebugTimeByToolId,
                        managedDebugRequired: true,
                        lastDebugSignature: null,
                        debugResult: emptyResult
                    }, () => this.persistToolDebugCache(this.state.toolDebugStatusByToolId, this.state.toolDebugTimeByToolId))
                    return
                }

                if (this.formRef.current) {
                    this.formRef.current.setFieldsValue({
                        jsonOutputExample: outputJsonText,
                        debugInputJson
                    })
                }

                this.setState(prev => ({
                    debugging: false,
                    parsedOutputs: outputs,
                    toolDebugRunResult: successResult,
                    toolDebugStatusByToolId: currentToolId ? {
                        ...prev.toolDebugStatusByToolId,
                        [currentToolId]: 'success'
                    } : prev.toolDebugStatusByToolId,
                    toolDebugTimeByToolId: currentToolId ? {
                        ...prev.toolDebugTimeByToolId,
                        [currentToolId]: new Date().toISOString()
                    } : prev.toolDebugTimeByToolId,
                    debugResult: successResult,
                    managedDebugRequired: false,
                    lastDebugSignature: this.computeManagedDebugSignature(nextValues),
                    examples: this.upsertManagedDebugExample(prev.examples, debugInputJson || '{}', outputJsonText),
                    formSnapshot: {
                        ...prev.formSnapshot,
                        debugInputJson
                    }
                }), () => this.persistToolDebugCache(this.state.toolDebugStatusByToolId, this.state.toolDebugTimeByToolId))
                return
            }

            this.setState({
                debugging: false,
                toolDebugRunResult: successResult,
                toolDebugStatusByToolId: currentToolId ? {
                    ...this.state.toolDebugStatusByToolId,
                    [currentToolId]: 'success'
                } : this.state.toolDebugStatusByToolId,
                toolDebugTimeByToolId: currentToolId ? {
                    ...this.state.toolDebugTimeByToolId,
                    [currentToolId]: new Date().toISOString()
                } : this.state.toolDebugTimeByToolId
            }, () => this.persistToolDebugCache(this.state.toolDebugStatusByToolId, this.state.toolDebugTimeByToolId))

            if (source === 'toolbar') {
                message.success('接口联调成功')
            }
        } catch (error) {
            const failedResult = {
                success: false,
                message: '接口调试失败: ' + this.getErrorMessage(error, '后端调试请求失败'),
                description: '请检查网关地址、鉴权请求头和测试入参是否正确。'
            }
            this.setState({
                debugging: false,
                toolDebugRunResult: failedResult,
                toolDebugStatusByToolId: currentToolId ? {
                    ...this.state.toolDebugStatusByToolId,
                    [currentToolId]: 'failed'
                } : this.state.toolDebugStatusByToolId,
                toolDebugTimeByToolId: currentToolId ? {
                    ...this.state.toolDebugTimeByToolId,
                    [currentToolId]: new Date().toISOString()
                } : this.state.toolDebugTimeByToolId,
                ...(currentApiMode === 'managed'
                    ? { managedDebugRequired: true, lastDebugSignature: null, debugResult: failedResult }
                    : {})
            }, () => this.persistToolDebugCache(this.state.toolDebugStatusByToolId, this.state.toolDebugTimeByToolId))
        }
    }

    handleToolDebugRun = async () => {
        const values = this.formRef.current?.getFieldsValue?.() || {}
        const selectedApi = (values.apiMode || this.state.apiMode) === 'bind_existing'
            ? this.getApiByCode(values.apiCode || this.state.selectedApiCode)
            : null
        const toolDebugFields = this.getToolDebugFields(values, selectedApi)

        let debugInputJson = this.state.toolDebugCodeInput || '{}'
        if (this.state.toolDebugInputMode === 'form') {
            try {
                debugInputJson = JSON.stringify(
                    this.buildToolDebugInputObjectFromParams(toolDebugFields, this.state.toolDebugParamValues),
                    null,
                    2
                )
            } catch (error) {
                this.setState({
                    toolDebugRunResult: {
                        success: false,
                        message: this.getErrorMessage(error, '调试入参组装失败')
                    }
                })
                return
            }
        }

        await this.executeToolDebug({ debugInputJson, source: 'modal' })
    }

    handleManagedDebug = async () => {
        this.openToolDebugModal()
    }

    previewSpec = async () => {
        if (!this.state.editRecord?.id) {
            message.warning('请先保存当前工具，再预览发布后的 spec')
            return
        }
        this.setState({ specPreviewVisible: true, specPreviewLoading: true, specPreviewData: null })
        try {
            const versionRes = await getMcpToolVersionList({ toolId: this.state.editRecord.id })
            const versions = this.normalizeVersionList(versionRes)
            if (!versions || !versions.length) {
                message.warning('当前工具还没有版本，无法预览 spec')
                this.setState({ specPreviewLoading: false })
                return
            }
            const spec = await getMcpToolPublishedSpec({ versionId: versions[0].id || versions[0].versionId })
            this.setState({ specPreviewData: spec, specPreviewLoading: false })
        } catch (error) {
            message.error('加载 spec 预览失败')
            this.setState({ specPreviewLoading: false })
        }
    }

    // 输入参数 Schema 变更时防抖自动解析
    handleJsonSchemaChange = (e) => {
        const raw = e.target.value
        if (this._jsonSchemaTimer) clearTimeout(this._jsonSchemaTimer)
        if (!raw || !raw.trim()) return
        this._jsonSchemaTimer = setTimeout(() => {
            const schema = this.safeParseJson(raw, null)
            if (!schema) return
            if (schema.type !== 'object' && !schema.properties) return
            const requestMeta = this.getCurrentRequestMeta()
            const params = this.buildTreeFieldsFromSchema(schema, 'paramName', false, '自动解析参数', requestMeta)
            this.setState({
                parsedParams: params,
                inputSchemaEmptyObject: this.isExplicitEmptyObjectSchema(schema)
            })
            this.markManagedDebugDirty('输入参数定义已变更，请重新调试接口')
            message.success(params.length ? '已自动识别并生成参数表' : '该 Schema 无输入参数', 1.5)
        }, 800)
    }

    // 输出参数 JSON 变更时防抖自动解析
    handleJsonOutputChange = (e) => {
        const raw = e.target.value
        if (this._jsonOutputTimer) clearTimeout(this._jsonOutputTimer)
        if (!raw || !raw.trim()) return
        this._jsonOutputTimer = setTimeout(() => {
            try {
                const parsed = JSON.parse(raw)
                const outputs = this.parseOutputFieldsFromResponse(parsed)
                this.setState({ parsedOutputs: outputs })
                message.success('已自动识别并生成输出字段', 1.5)
            } catch (e) {
                // 格式不合法时静默忽略，等用户输入完整再解析
            }
        }, 800)
    }

    // 输出参数 JSON 模板解析
    autoParseOutputJson = () => {
        const jsonStr = this.formRef.current?.getFieldValue('jsonOutputExample')
        if (!jsonStr) {
            message.warning('请先贴入 JSON 响应示例')
            return
        }
        try {
            const raw = JSON.parse(jsonStr)
            const outputs = this.parseOutputFieldsFromResponse(raw)
            this.setState({ parsedOutputs: outputs })
            message.success('已自动解析提取输出字段')
        } catch (e) {
            message.error('JSON格式不合法')
        }
    }

    // 测试工具调用
    handleTestTool = async (record) => {
        if (!record || !record.id) {
            message.warning('请先保存工具后再测试')
            return
        }
        this.setState({
            testing: true,
            testingToolId: record.id,
            testResult: null,
            testModalVisible: true,
            testTargetRecord: record
        })
        try {
            const versionRes = await getMcpToolVersionList({ toolId: record.id })
            const versions = this.normalizeVersionList(versionRes)
            const targetVersion = this.getPreferredTestVersion(versions, record)
            if (!targetVersion) {
                const isPublishedTool = this.normalizeVersionStatus(record?.status) === 'published'
                this.setState({
                    testResult: {
                        success: false,
                        message: isPublishedTool
                            ? '当前工具显示已上线，但没有查到可测试版本，请重新保存发布一次'
                            : '当前工具还没有可测试版本，请先保存发布',
                        description: '可点击下方“去设置”直接进入配置页处理。'
                    },
                    testing: false,
                    testingToolId: null
                })
                return
            }
            const versionId = targetVersion.id || targetVersion.versionId
            const targetVersionStatus = this.normalizeVersionStatus(targetVersion.status)

            if (targetVersionStatus !== 'published') {
                this.setState({
                    testResult: {
                        success: true,
                        message: '检测到当前版本尚未发布，正在先发布再测试...',
                        description: targetVersion.version ? `准备发布版本：${targetVersion.version}` : '准备发布最新版本'
                    }
                })
                try {
                    await publishMcpToolVersion({ versionId })
                } catch (publishError) {
                    this.setState({
                        testResult: {
                            success: false,
                            message: '发布失败，无法继续测试: ' + this.getErrorMessage(publishError, '版本发布失败'),
                            description: '请先检查发布配置是否完整，再重新测试。'
                        },
                        testing: false,
                        testingToolId: null
                    })
                    return
                }
            }
            // 先验证
            try {
                await validateMcpToolVersion({ versionId })
            } catch (e) {
                this.setState({
                    testResult: { success: false, message: '验证失败: ' + this.getErrorMessage(e, '版本校验未通过') },
                    testing: false,
                    testingToolId: null
                })
                return
            }
            // 再测试
            const result = await testMcpToolVersion({ versionId })
            this.setState({
                testResult: {
                    success: true,
                    data: result,
                    message: '测试调用成功',
                    description: [
                        targetVersion.version ? `当前测试版本：${targetVersion.version}` : '',
                        targetVersionStatus !== 'published' ? '本次已自动完成发布。' : ''
                    ].filter(Boolean).join(' ')
                },
                testing: false,
                testingToolId: null
            })
        } catch (error) {
            this.setState({
                testResult: { success: false, message: '测试失败: ' + this.getErrorMessage(error, '后端测试调用失败') },
                testing: false,
                testingToolId: null
            })
        }
    }

    openTestTargetEditor = () => {
        const { testTargetRecord } = this.state
        this.setState({ testModalVisible: false }, () => {
            if (testTargetRecord?.id) {
                this.openEdit(testTargetRecord)
            }
        })
    }

    scrollToEditorSection = (sectionId) => {
        this.setState({ activeEditorSection: sectionId })
        const target = document.getElementById(sectionId)
        if (target) {
            target.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
    }

    handleToolbarTest = () => {
        const values = this.formRef.current?.getFieldsValue?.() || {}
        const currentApiMode = values.apiMode || this.state.apiMode
        if (currentApiMode === 'managed' || currentApiMode === 'bind_existing') {
            this.openToolDebugModal()
            return
        }
        if (!this.state.editRecord) {
            message.warning('请先保存工具后再测试已发布版本')
            return
        }
        this.handleTestTool(this.state.editRecord)
    }

    handleSaveDraft = () => {
        this.submitIntent = 'draft'
        if (this.formRef.current) {
            this.formRef.current.submit()
        }
    }

    handlePublishSubmit = () => {
        this.submitIntent = 'publish'
        if (this.formRef.current) {
            this.formRef.current.submit()
        }
    }

    // 复制文本到剪贴板
    copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => message.success('已复制到剪贴板')).catch(() => message.error('复制失败'))
    }

    buildFallbackFieldDescription = (fieldName, prefix) => {
        if (!fieldName) return prefix
        return `${prefix}${fieldName}`
    }

    isManagedDraftApi = (api) => (api?.sourceSystem || '').trim() === 'McpToolAuto'

    getBindableApis = () => (this.state.apiDataSource || []).filter(api => !this.isManagedDraftApi(api))

    getApiByCode = (apiCode) => (this.state.apiDataSource || []).find(api => api.apiCode === apiCode)

    buildApiSummary = (api) => {
        if (!api) return null
        const requestSchema = this.safeParseJson(api.requestSchema)
        const params = this.parseParamDefinitions({
            schema: requestSchema,
            fieldNameKey: 'paramName',
            requestMeta: {
                method: api.method,
                url: this.getApiUrl(api)
            }
        }).fields

        return {
            apiName: api.apiName || api.apiCode,
            apiCode: api.apiCode,
            sourceSystem: api.sourceSystem || '未归类',
            method: this.normalizeRequestMethod(api.method),
            url: this.getApiUrl(api),
            paramCount: this.sanitizeParams(params, {
                method: api.method,
                url: this.getApiUrl(api)
            }).length
        }
    }

    getToolDebugFields = (values = {}, selectedApi) => {
        let sourceNodes = Array.isArray(this.state.parsedParams) ? this.state.parsedParams : []
        if (!sourceNodes.length && selectedApi?.requestSchema) {
            sourceNodes = this.parseParamDefinitions({
                schema: selectedApi.requestSchema,
                fieldNameKey: 'paramName',
                requestMeta: this.getRequestMetaFromValues(values, selectedApi)
            }).fields
        }

        return (sourceNodes || [])
            .map(node => ({
                key: node?.paramName || '',
                label: node?.paramName || '',
                description: node?.description || '',
                required: !!node?.required,
                paramType: node?.paramType || 'string',
                location: node?.location || this.resolveParamLocation({
                    item: node,
                    requestMeta: this.getRequestMetaFromValues(values, selectedApi)
                })
            }))
            .filter(item => item.key)
    }

    getInitialToolDebugInput = () => {
        const values = this.formRef.current?.getFieldsValue?.() || {}
        const exampleInput = (this.state.examples || []).find(item => (item?.inputJson || '').trim())?.inputJson
        return (values.debugInputJson || exampleInput || '{}').trim() || '{}'
    }

    normalizeDebugParamValue = (value, paramType) => {
        if (value === undefined || value === null || value === '') return undefined
        if (paramType === 'integer') return Number.parseInt(value, 10)
        if (paramType === 'number') return Number(value)
        if (paramType === 'boolean') return value === true || value === 'true'
        if (paramType === 'array' || paramType === 'object') {
            return typeof value === 'string' ? JSON.parse(value) : value
        }
        return value
    }

    buildToolDebugInputObjectFromParams = (fields = [], paramValues = {}) => {
        const payload = {}
        fields.forEach(field => {
            const rawValue = paramValues[field.key]
            if (rawValue === undefined || rawValue === null || rawValue === '') {
                if (field.required) {
                    throw new Error(`请填写参数 ${field.label}`)
                }
                return
            }
            payload[field.key] = this.normalizeDebugParamValue(rawValue, field.paramType)
        })
        return payload
    }

    openToolDebugModal = () => {
        const values = this.formRef.current?.getFieldsValue?.() || {}
        const currentApiMode = values.apiMode || this.state.apiMode
        const selectedApi = currentApiMode === 'bind_existing'
            ? this.getApiByCode(values.apiCode || this.state.selectedApiCode)
            : null

        if (currentApiMode === 'bind_existing' && !selectedApi) {
            message.warning('请先选择一个已有接口，再进行调试')
            return
        }

        const initialCodeInput = this.getInitialToolDebugInput()
        const toolDebugFields = this.getToolDebugFields(values, selectedApi)
        let initialParamValues = {}
        try {
            const parsed = this.parseDebugInputObject(initialCodeInput)
            initialParamValues = toolDebugFields.reduce((acc, field) => {
                const rawValue = parsed[field.key]
                if (rawValue === undefined) return acc
                acc[field.key] = field.paramType === 'array' || field.paramType === 'object'
                    ? JSON.stringify(rawValue, null, 2)
                    : String(rawValue)
                return acc
            }, {})
        } catch (e) {
            initialParamValues = {}
        }

        this.setState({
            toolDebugVisible: true,
            toolDebugInputMode: toolDebugFields.length ? 'form' : 'json',
            toolDebugCodeInput: initialCodeInput,
            toolDebugParamValues: initialParamValues,
            toolDebugRunResult: null
        })
    }

    closeToolDebugModal = () => {
        this.setState({
            toolDebugVisible: false,
            toolDebugRunResult: null
        })
    }

    handleToolDebugParamValueChange = (fieldKey, value) => {
        this.setState(prev => ({
            toolDebugParamValues: {
                ...prev.toolDebugParamValues,
                [fieldKey]: value
            }
        }))
    }

    handleToolDebugCodeInputChange = (value) => {
        this.setState({ toolDebugCodeInput: value })
    }

    openToolDebugForRecord = async (record) => {
        if (!record?.id) {
            message.warning('请先选择一个工具')
            return
        }
        await this.openEdit(record)
        setTimeout(() => {
            this.openToolDebugModal()
        }, 120)
    }

    ensureFieldDescriptions = (items = [], fieldKey, prefix) => items
        .map(item => ({
            ...item,
            description: (item?.description || '').trim() || this.buildFallbackFieldDescription(item?.[fieldKey], prefix)
        }))

    buildSchemaFromParamNode = (node) => {
        const nodeType = node?.paramType || 'string'
        if (nodeType === 'object') {
            const properties = {}
            const required = []
            ;(node?.children || []).forEach(child => {
                const childName = child?.paramName
                if (!childName) return
                properties[childName] = this.buildSchemaFromParamNode(child)
                if (child.required) required.push(childName)
            })
            const schema = { type: 'object', properties }
            if (required.length) schema.required = required
            return schema
        }
        if (nodeType === 'array') {
            const firstChild = Array.isArray(node?.children) && node.children.length > 0 ? node.children[0] : null
            return {
                type: 'array',
                items: firstChild ? this.buildSchemaFromParamNode(firstChild) : { type: 'string' }
            }
        }
        return {
            type: nodeType,
            description: (node?.description || '').trim() || undefined
        }
    }

    buildRequestSchemaFromParsedParams = (nodes = []) => {
        const properties = {}
        const required = []
        ;(nodes || []).forEach(node => {
            const key = node?.paramName
            if (!key) return
            properties[key] = this.buildSchemaFromParamNode(node)
            if (node.required) required.push(key)
        })
        const schema = { type: 'object', properties }
        if (required.length) schema.required = required
        return schema
    }

    buildSchemaFromExampleValue = (value) => {
        if (Array.isArray(value)) {
            return {
                type: 'array',
                items: value.length ? this.buildSchemaFromExampleValue(value[0]) : { type: 'string' }
            }
        }
        if (value === null || value === undefined) {
            return { type: 'string' }
        }
        if (typeof value === 'object') {
            const properties = {}
            Object.keys(value).forEach(key => {
                properties[key] = this.buildSchemaFromExampleValue(value[key])
            })
            return { type: 'object', properties }
        }
        if (typeof value === 'number') {
            return { type: Number.isInteger(value) ? 'integer' : 'number' }
        }
        if (typeof value === 'boolean') {
            return { type: 'boolean' }
        }
        return { type: 'string' }
    }

    getManagedDraftApiCode = (values = {}) => {
        const baseCode = (this.state.selectedApiCode || this.state.editRecord?.apiCode || values.toolCode || '').trim()
        if (!baseCode) return ''
        return baseCode
    }

    buildVersionPolicyJson = (values = {}) => JSON.stringify({
        authRequired: !!values.authRequired,
        userContext: !!values.userContext,
        maxRows: values.maxRows || 100,
        fixedHeaders: (this.state.fixedHeaders || [])
            .filter(item => (item?.headerName || '').trim())
            .map(item => ({
                headerName: (item.headerName || '').trim(),
                headerValue: item.headerValue || ''
            }))
    }, null, 2)

    buildDraftExamples = () => (this.state.examples || []).filter(item =>
        (item?.exampleName || '').trim() || (item?.userQuery || '').trim() || (item?.inputJson || '').trim() || (item?.outputJson || '').trim()
    ).map((item, index) => ({
        exampleName: (item.exampleName || '').trim() || `示例${index + 1}`,
        userQuery: item.userQuery || '',
        inputJson: item.inputJson || '{}',
        outputJson: item.outputJson || '{}',
        sortOrder: index + 1
    }))

    buildDraftDescription = (values, selectedApi) => {
        const current = (values.description || '').trim()
        if (current) return current
        const targetName = (values.name || '').trim()
            || (selectedApi?.apiName || '').trim()
            || (values.toolCode || '').trim()
            || '当前工具'
        return `${targetName}。当用户触发对应业务场景时调用该工具，按配置好的输入参数请求接口，并返回结构化结果字段供下游系统直接消费。`
    }

    getEffectiveParamAndOutputDraft = (values, selectedApi, parsedParams, parsedOutputs) => {
        const requestMeta = this.getRequestMetaFromValues(values, selectedApi)
        let effectiveParams = this.sanitizeParams(parsedParams, requestMeta)
        let effectiveOutputs = this.sanitizeOutputs(parsedOutputs)

        if ((values.apiMode || 'managed') === 'bind_existing') {
            if (!effectiveParams.length && selectedApi?.requestSchema) {
                effectiveParams = this.sanitizeParams(
                    this.parseParamDefinitions({
                        schema: selectedApi.requestSchema,
                        fieldNameKey: 'paramName',
                        defaultDescription: '输入参数：',
                        requestMeta
                    }).fields,
                    requestMeta
                )
            }
            if (!effectiveOutputs.length && selectedApi?.responseSchema) {
                effectiveOutputs = this.sanitizeOutputs(
                    this.parseParamDefinitions({
                        schema: selectedApi.responseSchema,
                        fieldNameKey: 'fieldName',
                        unwrapResponseEnvelope: true,
                        defaultDescription: '返回字段：'
                    }).fields
                )
            }
        }

        return {
            effectiveParams: this.ensureFieldDescriptions(effectiveParams, 'paramName', '输入参数：'),
            effectiveOutputs: this.ensureFieldDescriptions(effectiveOutputs, 'fieldName', '返回字段：')
        }
    }

    buildReadinessChecklist = (values = {}) => {
        const { parsedParams, parsedOutputs, apiDataSource } = this.state
        const selectedApi = apiDataSource.find(api => api.apiCode === values.apiCode)
        const { effectiveParams, effectiveOutputs } = this.getEffectiveParamAndOutputDraft(values, selectedApi, parsedParams, parsedOutputs)
        const issues = []
        const warnings = []
        const responseMappings = this.buildResponseMappingsFromOutputs(effectiveOutputs)
        const description = this.buildDraftDescription(values, selectedApi)

        if (!(values.name || '').trim()) issues.push('工具名称未填写')
        if (!(values.toolCode || '').trim()) issues.push('工具编码未填写')
        if (!description || description.trim().length < 10) issues.push('工具描述过短，需要说明用途、触发场景和返回结果')

        if ((values.apiMode || 'managed') === 'bind_existing') {
            if (!selectedApi) issues.push('还没有绑定 API')
            if (this.isManagedDraftApi(selectedApi)) issues.push('当前绑定的是自动托管接口，请改用正式接口或切换到托管模式')
        } else {
            if (!(values.method || '').trim()) issues.push('HTTP 方法未填写')
            if (!(values.url || '').trim()) issues.push('完整 API URL 未填写')
        }

        if (!effectiveOutputs.length) issues.push('至少需要 1 个输出字段，供下游理解返回结构')
        if (effectiveOutputs.some(item => !(item.description || '').trim())) issues.push('存在未填写说明的输出字段')
        if (!effectiveParams.length && !this.hasExplicitEmptyInputSchema(selectedApi, values.jsonExample)) {
            warnings.push('当前未识别到输入参数；如果这是无参接口可以忽略')
        }
        if (!responseMappings.length && effectiveOutputs.length) {
            warnings.push('建议补充 responseMapping，方便下游直接理解返回字段来源')
        }
        return { issues, warnings }
    }

    saveDraft = async (values) => {
        const { parsedParams, parsedOutputs, editRecord, apiDataSource, serviceInfo } = this.state
        const apiMode = values.apiMode || 'managed'
        const selectedApi = apiDataSource.find(api => api.apiCode === values.apiCode)
        const description = this.buildDraftDescription(values, selectedApi)
        const { effectiveParams, effectiveOutputs } = this.getEffectiveParamAndOutputDraft(values, selectedApi, parsedParams, parsedOutputs)
        const requestMeta = this.getRequestMetaFromValues(values, selectedApi)
        const sanitizedParams = this.ensureFieldDescriptions(this.sanitizeParams(effectiveParams, requestMeta), 'paramName', '输入参数：')
        const sanitizedOutputs = this.ensureFieldDescriptions(this.sanitizeOutputs(effectiveOutputs), 'fieldName', '返回字段：')
        const responseMappings = this.buildResponseMappingsFromOutputs(sanitizedOutputs)
        const draftExamples = this.buildDraftExamples()

        if (!(values.toolCode || '').trim()) {
            message.error('保存草稿前至少需要填写工具编码')
            return
        }
        if (!(values.name || '').trim()) {
            message.error('保存草稿前至少需要填写工具名称')
            return
        }
        if (!this.getServiceId()) {
            message.error('请先从具体 MCP 服务进入后再保存草稿')
            return
        }

        if (apiMode === 'bind_existing' && this.isManagedDraftApi(selectedApi)) {
            message.error('自动托管接口不能作为“复用已有接口”保存，请选择正式接口或切换到托管模式')
            return
        }

        let targetApi = selectedApi
        if (apiMode === 'managed') {
            if (!(values.method || '').trim() || !(values.url || '').trim()) {
                message.error('托管接口草稿至少需要填写 HTTP 方法和完整 API URL')
                return
            }
            const managedApiCode = this.getManagedDraftApiCode(values)
            if (!managedApiCode) {
                message.error('保存托管接口草稿前，请先填写工具编码')
                return
            }
            let baseUrl = values.url
            let path = '/'
            try {
                const urlObj = new URL(values.url)
                baseUrl = urlObj.origin
                path = urlObj.pathname + urlObj.search
            } catch (e) {
                message.error('完整 API URL 格式不合法')
                return
            }

            let requestSchemaText = '{}'
            const rawJsonSchema = (this.formRef.current?.getFieldValue('jsonExample') || '').trim()
            if (rawJsonSchema) {
                const parsedSchema = this.safeParseJson(rawJsonSchema)
                requestSchemaText = JSON.stringify(parsedSchema || this.buildRequestSchemaFromParsedParams(parsedParams), null, 2)
            } else {
                requestSchemaText = JSON.stringify(this.buildRequestSchemaFromParsedParams(parsedParams), null, 2)
            }

            let responseSchemaText = '{}'
            const rawOutputExample = (this.formRef.current?.getFieldValue('jsonOutputExample') || '').trim()
            if (rawOutputExample) {
                const parsedOutputExample = this.safeParseJson(rawOutputExample)
                responseSchemaText = JSON.stringify(this.buildSchemaFromExampleValue(parsedOutputExample || {}), null, 2)
            } else if (sanitizedOutputs.length) {
                responseSchemaText = JSON.stringify({
                    type: 'object',
                    properties: sanitizedOutputs.reduce((acc, item) => {
                        acc[item.fieldName] = { type: item.paramType || 'string', description: item.description }
                        return acc
                    }, {})
                }, null, 2)
            }

            const existingManagedApi = apiDataSource.find(api =>
                api.apiCode === managedApiCode || (editRecord?.apiCode && api.apiCode === editRecord.apiCode)
            )
            const apiPayload = {
                apiCode: managedApiCode,
                apiName: (values.name || '').trim(),
                sourceSystem: 'McpToolAuto',
                protocol: 'http',
                method: values.method,
                path,
                baseUrl,
                apiVersion: 'draft',
                requestSchema: requestSchemaText,
                responseSchema: responseSchemaText,
                authType: values.authRequired ? 'INTERNAL' : 'NONE',
                isPublic: !values.authRequired,
                timeoutMs: 10000,
                owner: serviceInfo?.owner || serviceInfo?.serviceName || 'McpTool',
                description
            }

            targetApi = existingManagedApi?.id
                ? await updateApiRegistry({ id: existingManagedApi.id, ...apiPayload })
                : await saveApiRegistry(apiPayload)
        }

        const toolPayload = {
            serviceId: this.getServiceId(),
            toolCode: values.toolCode,
            name: values.name,
            category: values.category || '其他',
            description
        }

        const savedTool = editRecord?.id
            ? await updateMcpTool({ id: editRecord.id, ...toolPayload })
            : await saveMcpTool(toolPayload)

        const versionPayload = {
            toolId: savedTool.id || editRecord?.id,
            apiId: targetApi?.id,
            apiVersion: targetApi?.apiVersion || 'draft',
            description,
            responseMapping: JSON.stringify(responseMappings.reduce((acc, item) => {
                acc[item.fieldName] = item.jsonPath
                return acc
            }, {}), null, 2),
            policyJson: this.buildVersionPolicyJson(values),
            params: sanitizedParams.map((p, index) => ({
                paramName: p.paramName,
                type: p.paramType || 'string',
                description: p.description,
                required: !!p.required,
                location: p.location || undefined,
                sortOrder: index + 1
            })),
            outputs: sanitizedOutputs.map((p, index) => ({
                fieldName: p.fieldName,
                type: p.paramType || 'string',
                description: p.description,
                sortOrder: index + 1
            })),
            examples: draftExamples
        }

        await saveMcpToolVersion(versionPayload)
        message.success('草稿已保存')
        this.setState({
            editRecord: {
                ...(editRecord || {}),
                ...savedTool,
                apiCode: apiMode === 'bind_existing' ? values.apiCode : targetApi?.apiCode
            },
            selectedApiCode: apiMode === 'bind_existing' ? values.apiCode : (targetApi?.apiCode || this.state.selectedApiCode)
        })
        await Promise.all([this.loadApiList(), this.loadList()])
    }

    handleSaveAndLive = async (values) => {
        this.setState({ saving: true })
        try {
            if (this.submitIntent === 'draft') {
                await this.saveDraft(values)
                return
            }
            const { parsedParams, parsedOutputs, editRecord, apiDataSource } = this.state
            const apiMode = values.apiMode || 'managed'
            const selectedApi = apiDataSource.find(api => api.apiCode === values.apiCode)
            const description = this.buildDraftDescription(values, selectedApi)
            const { effectiveParams, effectiveOutputs } = this.getEffectiveParamAndOutputDraft(values, selectedApi, parsedParams, parsedOutputs)
            const sanitizedParams = effectiveParams
            const sanitizedOutputs = effectiveOutputs
            const responseMappings = this.buildResponseMappingsFromOutputs(sanitizedOutputs)
            const examples = (this.state.examples || []).filter(item =>
                (item?.exampleName || '').trim() || (item?.userQuery || '').trim() || (item?.inputJson || '').trim() || (item?.outputJson || '').trim()
            )
            const hasRequestSchema = sanitizedParams.length > 0
            const hasResponseSchema = sanitizedOutputs.length > 0
            const hasExplicitEmptyInputSchema = this.hasExplicitEmptyInputSchema(selectedApi, values.jsonExample)

            if (!description) {
                message.error('工具描述（description）不能为空，它是模型理解工具用途的关键')
                return
            }

            if (apiMode === 'managed' && this.state.managedDebugRequired) {
                message.error('请先调试接口并成功生成输出字段，再保存发布')
                return
            }

            if (apiMode === 'bind_existing') {
                if (!selectedApi) {
                    message.error('请先选择一个已注册 API')
                    return
                }
                if (this.isManagedDraftApi(selectedApi)) {
                    message.error('自动托管接口不能作为“复用已有接口”发布，请改用正式接口或切换到托管模式')
                    return
                }
                if (!selectedApi.requestSchema || !selectedApi.responseSchema) {
                    message.error(`绑定接口 [${values.apiCode}] 尚未配置完整的 request/response schema，请先到接口库补齐`)
                    return
                }
                if ((!hasRequestSchema && !hasExplicitEmptyInputSchema) || !hasResponseSchema) {
                    message.error('当前工具的输入/输出参数未生成完整，请重新选择接口或手动补齐请求与响应字段')
                    return
                }
            } else {
                let parsedPath = values.url
                try {
                    parsedPath = new URL(values.url).pathname + new URL(values.url).search
                } catch (e) {
                    // keep original value for fallback compare
                }
                const duplicateOfficialApi = apiDataSource.find(api => {
                    return api.sourceSystem !== 'McpToolAuto'
                        && (api.method || '').toUpperCase() === values.method.toUpperCase()
                        && (api.path || '') === parsedPath
                })
                if (duplicateOfficialApi) {
                    message.error(`检测到同路由正式接口 [${duplicateOfficialApi.apiCode}]，请改用“绑定已有 API”模式`)
                    return
                }
            }

            // 1. 解析URL (例如 https://gateway.com/v1/user)
            let baseUrl = values.url
            let path = '/'
            try {
                const urlObj = new URL(values.url)
                baseUrl = urlObj.origin
                path = urlObj.pathname + urlObj.search
            } catch(e) {
                // Ignore, fallback
            }

            // 2. 构造聚合请求体
            const payload = {
                serviceId: this.getServiceId(),
                apiMode,
                toolCode: values.toolCode,
                name: values.name,
                description,
                category: values.category || '其他',
                authRequired: !!values.authRequired,
                userContext: !!values.userContext,
                maxRows: values.maxRows || 100,
                fixedHeaders: (this.state.fixedHeaders || []).filter(item => (item?.headerName || '').trim()).map(item => ({
                    headerName: (item.headerName || '').trim(),
                    headerValue: item.headerValue || ''
                })),
                responseMapping: JSON.stringify(responseMappings.reduce((acc, item) => {
                    acc[item.fieldName] = item.jsonPath
                    return acc
                }, {}), null, 2),
                examples: examples.map((item, index) => ({
                    exampleName: (item.exampleName || '').trim() || `示例${index + 1}`,
                    userQuery: item.userQuery || '',
                    inputJson: item.inputJson || '{}',
                    outputJson: item.outputJson || '{}',
                    sortOrder: index + 1
                })),
                params: sanitizedParams.map(p => ({
                    paramName: p.paramName,
                    type: p.paramType || 'string',
                    description: p.description,
                    required: !!p.required,
                    location: p.location || undefined
                })),
                outputs: sanitizedOutputs.map(p => ({
                    fieldName: p.fieldName,
                    type: p.paramType || 'string',
                    description: p.description
                }))
            }
            if (apiMode === 'bind_existing') {
                payload.apiCode = values.apiCode
            } else {
                payload.method = values.method.toUpperCase()
                payload.baseUrl = baseUrl
                payload.path = path
            }

            // 3. 区分新建 vs 编辑
            if (editRecord && editRecord.id) {
                payload.id = editRecord.id
            }
            await createToolCombo(payload)

            message.success('Tool 已自动保存并在远端一键发布！')
            // 展示连接信息
            this.setState({
                drawerVisible: false,
                publishInfoVisible: true,
                publishedTool: { toolCode: values.toolCode, name: values.name }
            })
            this.loadList()

        } catch (error) {
            console.error(error)
            message.error(error?.message || '自动编排保存失败，请检查网络或联系管理员')
        } finally {
            this.setState({ saving: false })
        }
    }

    render() {
        const { dataSource, loading, saving, drawerVisible, parsedParams, apiDataSource,
            testing, testingToolId, testResult, testModalVisible, testTargetRecord,
            publishInfoVisible, publishedTool, serviceInfo, selectedApiCode, apiMode,
            debugging, debugResult, managedDebugRequired, activeEditorSection,
            toolDebugVisible, toolDebugInputMode, toolDebugCodeInput, toolDebugParamValues, toolDebugRunResult,
            toolDebugStatusByToolId, toolDebugTimeByToolId } = this.state

        const bindableApis = this.getBindableApis()
        const apiGroups = {}
        bindableApis.forEach(api => {
            const sys = api.sourceSystem || '未分类 (Default)'
            if (!apiGroups[sys]) apiGroups[sys] = []
            apiGroups[sys].push(api)
        })

        const serviceId = this.getServiceId()
        const editingExistingTool = !!this.state.editRecord?.id
        const hasExplicitServiceContext = !!(serviceInfo || serviceId)
        const mcpEndpoint = serviceInfo
            ? `${window.location.origin}/lechun-mcp/mcp/${serviceInfo.serviceCode}/sse`
            : (serviceId ? `${window.location.origin}/lechun-mcp/mcp/sse` : null)
        const currentProjectLabel = serviceInfo?.serviceName
            || serviceInfo?.serviceCode
            || (serviceId ? `服务 #${serviceId}` : (editingExistingTool ? '全部工具列表' : '未选择项目'))
        const shouldShowMissingServiceWarning = !hasExplicitServiceContext && !editingExistingTool
        const selectedBindApi = apiMode === 'bind_existing' ? this.getApiByCode(selectedApiCode) : null
        const selectedBindApiSummary = this.buildApiSummary(selectedBindApi)
        const currentFormValues = this.formRef.current?.getFieldsValue?.() || this.state.formSnapshot || {}
        const currentDebugApi = (currentFormValues.apiMode || apiMode) === 'bind_existing'
            ? this.getApiByCode(currentFormValues.apiCode || selectedApiCode)
            : null
        const toolDebugFields = this.getToolDebugFields(currentFormValues, currentDebugApi)
        const apiModeCards = [
            {
                key: 'bind_existing',
                title: '选择已有接口',
                description: '从已初始化到数据表的接口里选一个，自动带入地址、方法、参数和说明。',
                hint: '主路径，适合直接绑定现成 HTTP 接口'
            },
            {
                key: 'managed',
                title: '新建接口',
                description: '当库里没有合适接口时，再手工填写地址和方法并联调生成输出。',
                hint: '兜底路径，适合新增 HTTP 接口'
            }
        ]

        const sectionStyle = {
            background: '#fff',
            border: '1px solid #edf0f5',
            borderRadius: 16,
            padding: 24,
            marginBottom: 20,
            scrollMarginTop: 96
        }
        const sectionTitleStyle = { fontSize: 16, fontWeight: 700, marginBottom: 18, color: '#1f2a3d' }
        const editorSections = [
            { key: 'tool-info', label: '工具信息' },
            { key: 'input-params', label: '输入参数' },
            { key: 'output-params', label: '输出参数' },
            { key: 'advanced-config', label: '高级配置' }
        ]
        const currentStatus = this.state.editRecord?.status
            || (managedDebugRequired ? 'draft' : (apiMode === 'managed' ? 'tested' : this.state.editRecord?.status || 'draft'))
        const statusConfig = currentStatus === 'published'
            ? { color: 'green', label: '已发布' }
            : currentStatus === 'tested'
                ? { color: 'blue', label: '已测试' }
                : { color: 'default', label: '草稿' }

        const columns = [
            {
                title: '工具资产',
                key: 'tool',
                width: 420,
                render: (_, r) => (
                    <div>
                        <div style={{ fontWeight: 600, fontSize: 16 }}>{r.name || r.toolName}</div>
                        <div style={{ color: '#666', fontSize: 13, marginTop: 4 }}>{r.description || '暂无描述'}</div>
                        <Space style={{ marginTop: 8 }}>
                            <Tag color="blue">{r.category || '其他'}</Tag>
                            <Tag color="cyan">{r.toolCode}</Tag>
                            {r.status === 'published' ? <Tag color="green"><CheckCircleOutlined/> 一键在线</Tag> : <Tag color="default"><StopOutlined/> 停用</Tag>}
                        </Space>
                    </div>
                )
            },
            {
                title: '已绑定接口',
                key: 'api',
                render: (_, r) => {
                    const linkedApi = this.getApiByCode(r.apiCode)
                    if (!r.apiCode) {
                        return <Text type="secondary">未绑定</Text>
                    }
                    return (
                        <div>
                            <div style={{ fontWeight: 600, color: '#223046' }}>{linkedApi?.apiName || r.apiCode}</div>
                            <div style={{ color: '#667085', fontSize: 12, marginTop: 4 }}>
                                {(linkedApi?.method || r.method || 'POST').toUpperCase()} {linkedApi?.path || r.url || '-'}
                            </div>
                            <Space size={6} style={{ marginTop: 8, flexWrap: 'wrap' }}>
                                <Tag color={this.isManagedDraftApi(linkedApi) ? 'gold' : 'processing'}>
                                    {this.isManagedDraftApi(linkedApi) ? '新建接口' : '已有接口'}
                                </Tag>
                                {linkedApi?.sourceSystem ? <Tag>{linkedApi.sourceSystem}</Tag> : null}
                                <Text code style={{ fontSize: 11 }}>{r.apiCode}</Text>
                            </Space>
                        </div>
                    )
                }
            },
            {
                title: '维护操作',
                key: 'action',
                width: 240,
                render: (_, r) => (
                    <Space size={8}>
                        <Button type="primary" ghost icon={<EditOutlined />} onClick={() => this.openEdit(r)}>设置</Button>
                        <Button
                            icon={<BugOutlined />}
                            loading={debugging && this.state.editRecord?.id === r.id}
                            onClick={() => this.openToolDebugForRecord(r)}
                        >
                            调试
                        </Button>
                    </Space>
                )
            },
            {
                title: '调试状态',
                key: 'debugStatus',
                width: 120,
                render: (_, r) => {
                    const status = toolDebugStatusByToolId[r.id]
                    if (status === 'success') return <Tag color="green">成功</Tag>
                    if (status === 'failed') return <Tag color="red">失败</Tag>
                    return <Text type="secondary">未调试</Text>
                }
            }
        ]

        return (
            <div style={{ padding: 16 }}>
                <Row align="middle" justify="space-between" style={{ marginBottom: 16 }}>
                    <Col>
                        <div style={{ fontSize: 24, fontWeight: 700 }}>MCP Tool 开发者工作台</div>
                        <div style={{ color: '#666', marginTop: 4 }}>
                            一个服务下可以新建多个 Tool；每个 Tool 绑定一个 HTTP 接口，优先从库里选择已有接口，没有再新建。
                            <Tag color={serviceInfo ? 'blue' : (editingExistingTool ? 'default' : 'warning')} style={{ marginLeft: 8 }}>
                                当前项目：{currentProjectLabel}
                            </Tag>
                        </div>
                    </Col>
                    <Col>
                        <Space>
                            <Button icon={<ApiOutlined />} onClick={this.goToApiRegistry}>接口管理</Button>
                            <Button icon={<ReloadOutlined />} onClick={this.loadList}>刷新</Button>
                            <Button type="primary" size="large" icon={<PlusOutlined />} onClick={this.openCreate}>新建 Tool</Button>
                        </Space>
                    </Col>
                </Row>

                <Card bordered={false} style={{ borderRadius: 16 }}>
                    <Table
                        dataSource={dataSource}
                        columns={columns}
                        rowKey="id"
                        loading={loading}
                        pagination={{ pageSize: 20 }}
                    />
                </Card>

                {/* ======== 工具编辑 Drawer ======== */}
                <Drawer
                    title={(
                        <Space size={12}>
                            <span>{this.state.editRecord ? '编辑 MCP Tool' : '新建 MCP Tool'}</span>
                            <Tag color={statusConfig.color}>{statusConfig.label}</Tag>
                        </Space>
                    )}
                    width={1280}
                    visible={drawerVisible}
                    onClose={() => this.setState({ drawerVisible: false })}
                    footer={null}
                    destroyOnClose
                >
                    <Form ref={this.formRef} layout="vertical" onFinish={this.handleSaveAndLive}
                        onValuesChange={(changedValues, allValues) => {
                            this.setState({ formSnapshot: allValues })
                            if (
                                apiMode === 'managed'
                                && Object.prototype.hasOwnProperty.call(changedValues, 'debugInputJson')
                            ) {
                                this.markManagedDebugDirty('测试入参已变更，请重新调试接口')
                            }
                        }}
                        onFinishFailed={() => message.warning('保存失败：请往上滑动，检查上面的必填项（红星指示）是否已填写完整！')}>

                        <div style={{
                            position: 'sticky',
                            top: -24,
                            zIndex: 8,
                            background: 'rgba(255,255,255,0.96)',
                            backdropFilter: 'blur(8px)',
                            margin: '-24px -24px 20px',
                            padding: '20px 24px 16px',
                            borderBottom: '1px solid #edf0f5'
                        }}>
                            <Row justify="space-between" align="middle" gutter={16}>
                                <Col>
                                    <div style={{ fontSize: 22, fontWeight: 700, color: '#1f2a3d' }}>
                                        {this.state.editRecord ? (this.state.editRecord.name || this.state.editRecord.toolCode || '编辑工具') : '创建新工具'}
                                    </div>
                                    <div style={{ color: '#7a8599', marginTop: 6 }}>
                                        填写配置后，先测试，再发布。
                                    </div>
                                </Col>
                                <Col>
                                    <Space>
                                        <Button size="large" onClick={this.handleSaveDraft}>保存草稿</Button>
                                        <Button size="large" icon={<BugOutlined />} loading={debugging || (testing && !!testingToolId)} onClick={this.handleToolbarTest}>
                                            测试工具
                                        </Button>
                                        <Button type="primary" size="large" icon={<ThunderboltOutlined />} loading={saving} onClick={this.handlePublishSubmit}>
                                            发布
                                        </Button>
                                    </Space>
                                </Col>
                            </Row>
                        </div>

                        {shouldShowMissingServiceWarning && (
                            <Alert
                                type="warning"
                                showIcon
                                message="当前未关联任何服务，创建的工具可能无法被 MCP Server 暴露。建议从「MCP服务管理」进入。"
                                style={{ marginBottom: 16 }}
                            />
                        )}

                        <Row gutter={24} align="top">
                            <Col flex="auto">
                        {/* 接口来源选择 */}
                        <div style={{ background: '#f8fbff', padding: 20, borderRadius: 16, marginBottom: 20, border: '1px solid #e6eefc' }}>
                            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 16, color: '#175ec9' }}>
                                <span role="img" aria-label="magic">✨</span> 为当前 Tool 选择接口来源
                            </div>
                            <Form.Item name="apiMode" initialValue="bind_existing" style={{ marginBottom: 16 }}>
                                <div>
                                    <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#223046' }}>先决定当前 Tool 绑定已有 HTTP 接口，还是新建一条 HTTP 接口</div>
                                    <Row gutter={12}>
                                        {apiModeCards.map(card => {
                                            const selected = apiMode === card.key
                                            return (
                                                <Col span={12} key={card.key}>
                                                    <div
                                                        onClick={() => this.handleApiModeChange(card.key)}
                                                        style={{
                                                            cursor: 'pointer',
                                                            height: '100%',
                                                            borderRadius: 12,
                                                            padding: 16,
                                                            border: selected ? '2px solid #1677ff' : '1px solid #d9e5f5',
                                                            background: selected ? '#f0f7ff' : '#fff',
                                                            boxShadow: selected ? '0 4px 14px rgba(22,119,255,0.08)' : 'none'
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                                            <div style={{ fontSize: 16, fontWeight: 600, color: '#1f2a3d' }}>{card.title}</div>
                                                            {selected && <Tag color="blue" style={{ marginRight: 0 }}>当前选择</Tag>}
                                                        </div>
                                                        <div style={{ color: '#4b5b76', lineHeight: 1.6, minHeight: 44 }}>{card.description}</div>
                                                        <div style={{ marginTop: 10, color: '#7a879a', fontSize: 12 }}>{card.hint}</div>
                                                    </div>
                                                </Col>
                                            )
                                        })}
                                    </Row>
                                </div>
                            </Form.Item>

                            {apiMode === 'bind_existing' && (
                                <>
                                    <Form.Item name="apiCode" label="选择一个已有接口" rules={[{ required: true, message: '请选择一个已注册 API' }]} style={{ marginBottom: 0 }}>
                                        <Select
                                            showSearch
                                            allowClear
                                            placeholder="按所属系统分类搜索接口名称或编码"
                                            onChange={this.handleApiSelect}
                                            optionFilterProp="label"
                                            size="large"
                                        >
                                            {Object.keys(apiGroups).map(sys => (
                                                <Select.OptGroup label={sys} key={sys}>
                                                    {apiGroups[sys].map(api => (
                                                        <Select.Option
                                                            key={api.apiCode}
                                                            value={api.apiCode}
                                                            label={`${api.apiName} ${api.apiCode} ${api.method || ''} ${api.path || ''} ${api.sourceSystem || ''}`}
                                                        >
                                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                <span>{api.apiName}</span>
                                                                <span style={{ color: '#999', fontSize: 12 }}>{api.apiCode}</span>
                                                            </div>
                                                            <div style={{ color: '#98a2b3', fontSize: 12, marginTop: 4 }}>
                                                                {(api.method || 'POST').toUpperCase()} {api.path || '/'} · {api.sourceSystem || '未归类'}
                                                            </div>
                                                        </Select.Option>
                                                    ))}
                                                </Select.OptGroup>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                    {selectedBindApiSummary && (
                                        <Alert
                                            style={{ marginTop: 12, marginBottom: 12 }}
                                            type="success"
                                            showIcon
                                            message={`已选择接口：${selectedBindApiSummary.apiName}`}
                                            description={(
                                                <div>
                                                    <div><Text strong>接口编码：</Text>{selectedBindApiSummary.apiCode}</div>
                                                    <div><Text strong>来源系统：</Text>{selectedBindApiSummary.sourceSystem}</div>
                                                    <div><Text strong>请求方式：</Text>{selectedBindApiSummary.method}</div>
                                                    <div><Text strong>接口地址：</Text>{selectedBindApiSummary.url}</div>
                                                    <div><Text strong>识别到输入参数：</Text>{selectedBindApiSummary.paramCount} 个</div>
                                                </div>
                                            )}
                                        />
                                    )}
                                    <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: selectedBindApiSummary ? 6 : 12 }}>
                                        · 共 {bindableApis.length} 个可选接口，仅绑定，不新建、不改动接口中心配置
                                    </div>
                                </>
                            )}
                            {apiMode === 'managed' && (
                                <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 8 }}>
                                    · 将新建一条 HTTP 接口，测试成功后自动生成输出参数
                                </div>
                            )}
                        </div>

                        {/* 1. 基础配置 */}
                        <div id="tool-info" style={sectionStyle}>
                            <div style={sectionTitleStyle}>工具信息</div>
                            {!serviceInfo && !editingExistingTool && (
                                <div style={{ fontSize: 12, color: '#fa8c16', marginBottom: 12 }}>
                                    ◆ 请先从具体 MCP 服务进入后再创建 Tool，否则无法确定归属服务
                                </div>
                            )}
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name="toolCode" label="唯一标识代码 (英文字母+下划线)" rules={[{ required: true }]}>
                                        <Input placeholder="eg: weather_query" disabled={!!this.state.editRecord} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name="name" label="工具展示名称" rules={[{ required: true }]}>
                                        <Input placeholder="eg: 全球天气查询仪" />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Form.Item name="description" label="详细Prompt描述 (建议填写，不填时系统会自动补一版默认描述)">
                                <TextArea rows={3} placeholder="告诉模型这个工具是做什么的，在何种上下文中调用它。例如：当用户询问任何关于天气的状况时，调用此工具。" />
                            </Form.Item>
                            {(() => {
                                const readiness = this.buildReadinessChecklist(this.state.formSnapshot || {})
                                if (!readiness.issues.length && !readiness.warnings.length) return null
                                return (
                                    <div style={{ marginTop: 4, paddingLeft: 2 }}>
                                        {readiness.issues.map(item => (
                                            <div key={item} style={{ fontSize: 12, color: '#fa8c16', display: 'flex', alignItems: 'baseline', gap: 5, marginBottom: 3 }}>
                                                <span style={{ flexShrink: 0 }}>◆</span><span>{item}</span>
                                            </div>
                                        ))}
                                        {readiness.warnings.map(item => (
                                            <div key={item} style={{ fontSize: 12, color: '#8c8c8c', display: 'flex', alignItems: 'baseline', gap: 5, marginBottom: 3 }}>
                                                <span style={{ flexShrink: 0 }}>·</span><span>{item}</span>
                                            </div>
                                        ))}
                                    </div>
                                )
                            })()}
                        </div>

                        <div style={{ marginBottom: 24 }}>
                            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#4b5b76' }}>接口连接</div>
                            <Row gutter={16}>
                                <Col span={6}>
                                    <Form.Item name="method" label={apiMode === 'bind_existing' ? 'HTTP方式（自动带入）' : 'HTTP方式'} rules={apiMode === 'managed' ? [{ required: true }] : []}>
                                        <Select
                                            disabled={apiMode === 'bind_existing'}
                                            onChange={value => this.handleRequestMetaFieldChange('method', value)}
                                            options={['GET','POST','PUT','DELETE'].map(x => ({label:x, value:x}))}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={18}>
                                    <Form.Item name="url" label={apiMode === 'bind_existing' ? '完整 API URL（自动带入）' : '完整 API URL'} rules={apiMode === 'managed' ? [{ required: true, type: 'url' }] : []}>
                                        <Input
                                            disabled={apiMode === 'bind_existing'}
                                            onChange={e => this.handleRequestMetaFieldChange('url', e.target.value)}
                                            placeholder="https://api.example.com/v1/weather"
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                            {apiMode === 'bind_existing' && selectedApiCode && (
                                <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 6 }}>
                                    · 已绑定现有 API，地址与方式以接口中心为准；如需手工填写请切换到”新建接口”
                                </div>
                            )}
                        </div>

                        {/* 3. 输入参数 */}
                        <div id="input-params" style={sectionStyle}>
                            <div style={sectionTitleStyle}>输入参数</div>

                            <div style={{ marginBottom: 12 }}>
                                <Button
                                    type="link"
                                    size="small"
                                    icon={<CodeOutlined />}
                                    style={{ paddingLeft: 0, fontSize: 13 }}
                                    onClick={() => this.setState(s => ({ jsonSchemaImportVisible: !s.jsonSchemaImportVisible }))}
                                >
                                    {this.state.jsonSchemaImportVisible ? '收起 JSON Schema 导入' : '从 JSON Schema 导入参数'}
                                </Button>
                            </div>
                            {this.state.jsonSchemaImportVisible && (
                                <Form.Item name="jsonExample" style={{ marginBottom: 12 }}>
                                    <TextArea
                                        rows={6}
                                        placeholder={`贴入 JSON Schema，自动识别参数（支持嵌套）\n\n{\n  "type": "object",\n  "properties": {\n    "keyword": { "type": "string", "description": "搜索关键词" }\n  }\n}`}
                                        onChange={this.handleJsonSchemaChange}
                                    />
                                </Form.Item>
                            )}
                            {this.state.inputSchemaEmptyObject && (
                                <Alert
                                    type="info"
                                    showIcon
                                    style={{ marginBottom: 12 }}
                                    message="该接口无输入参数，系统已自动带入 {}"
                                />
                            )}

                            <Table
                                size="small"
                                pagination={false}
                                dataSource={parsedParams}
                                rowKey="key"
                                childrenColumnName="children"
                                defaultExpandAllRows
                                columns={[
                                    {
                                        title: '参数名称',
                                        dataIndex: 'paramName',
                                        width: 220,
                                        render: (v, r) => (
                                            <div>
                                                <Input
                                                    size="small"
                                                    value={v}
                                                    onChange={e => this.handleParamChange(r.key, 'paramName', e.target.value)}
                                                />
                                                {r.fullPath && r.fullPath !== v && (
                                                    <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                                                        路径：{r.fullPath}
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    },
                                    {
                                        title: '类型',
                                        dataIndex: 'paramType',
                                        width: 110,
                                        render: (v, r) => (
                                            <Select
                                                size="small"
                                                value={v}
                                                onChange={val => this.handleParamChange(r.key, 'paramType', val)}
                                                options={['string', 'number', 'integer', 'boolean', 'array', 'object'].map(x => ({
                                                    label: x,
                                                    value: x
                                                }))}
                                            />
                                        )
                                    },
                                    {
                                        title: '位置',
                                        dataIndex: 'location',
                                        width: 100,
                                        render: (v, r) => (
                                            <Select
                                                size="small"
                                                value={v || this.resolveParamLocation({ item: r, requestMeta: this.getCurrentRequestMeta() })}
                                                onChange={val => this.handleParamChange(r.key, 'location', val)}
                                                options={['query', 'body', 'path', 'header'].map(x => ({
                                                    label: x,
                                                    value: x
                                                }))}
                                            />
                                        )
                                    },
                                    {
                                        title: '必填',
                                        dataIndex: 'required',
                                        width: 80,
                                        render: (v, r) => (
                                            <Switch
                                                size="small"
                                                checked={!!v}
                                                onChange={val => this.handleParamChange(r.key, 'required', val)}
                                            />
                                        )
                                    },
                                    {
                                        title: '用途描述 (给AI看)',
                                        dataIndex: 'description',
                                        render: (v, r) => (
                                            <Input
                                                size="small"
                                                value={v}
                                                onChange={e => this.handleParamChange(r.key, 'description', e.target.value)}
                                            />
                                        )
                                    },
                                    {
                                        title: '',
                                        width: 50,
                                        render: (_, r) => (
                                            <Button
                                                size="small"
                                                danger
                                                type="text"
                                                icon={<DeleteOutlined />}
                                                onClick={() => this.removeParam(r.key)}
                                            />
                                        )
                                    }
                                ]}
                            />
                            <Button type="dashed" block size="small" icon={<PlusOutlined />} onClick={this.addEmptyParam} style={{ marginTop: 8 }}>
                                手工添加一行
                            </Button>
                        </div>

                        {apiMode === 'managed' && (
                            <div style={{ marginTop: 20 }}>
                                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#4b5b76' }}>
                                    联调验证
                                </div>
                                <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                                    填一组真实测试入参，系统会直接请求接口；调用成功后自动回填响应 JSON 并生成输出字段。
                                </Text>
                                <Form.Item name="debugInputJson" label="测试入参 JSON" initialValue="{}">
                                    <TextArea
                                        rows={6}
                                        placeholder={`{
  "id": 123,
  "keyword": "联调"
}`}
                                    />
                                </Form.Item>
                                <Button
                                    type="primary"
                                    ghost
                                    icon={<BugOutlined />}
                                    onClick={this.openToolDebugModal}
                                >
                                    打开调试面板
                                </Button>
                                {managedDebugRequired && (
                                    <Alert
                                        type="warning"
                                        showIcon
                                        style={{ marginTop: 12 }}
                                        message="当前接口配置尚未完成联调"
                                        description="新建托管接口或修改接口地址、请求头、参数定义后，都需要重新调试一次，确认返回结构可用后才能发布。"
                                    />
                                )}
                                {debugResult && (
                                    <div style={{ marginTop: 12 }}>
                                        <Alert
                                            type={debugResult.success ? 'success' : 'error'}
                                            showIcon
                                            message={debugResult.message}
                                            description={debugResult.description}
                                            style={{ marginBottom: debugResult.data ? 12 : 0 }}
                                        />
                                        {debugResult.data && (
                                            <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 8, maxHeight: 240, overflow: 'auto' }}>
                                                <pre style={{ margin: 0, fontSize: 12 }}>{JSON.stringify(debugResult.data, null, 2)}</pre>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* 4. 输出参数 */}
                        <div id="output-params" style={sectionStyle}>
                            <div style={sectionTitleStyle}>输出参数</div>

                            {apiMode === 'managed' ? (
                                <>
                                    <div style={{ marginBottom: 12 }}>
                                        <Button
                                            type="link"
                                            size="small"
                                            icon={<CodeOutlined />}
                                            style={{ paddingLeft: 0, fontSize: 13 }}
                                            onClick={() => this.setState(s => ({ jsonOutputImportVisible: !s.jsonOutputImportVisible }))}
                                        >
                                            {this.state.jsonOutputImportVisible ? '收起 JSON 导入' : '从接口返回 JSON 导入字段'}
                                        </Button>
                                    </div>
                                    {this.state.jsonOutputImportVisible && (
                                        <Form.Item name="jsonOutputExample" style={{ marginBottom: 12 }}>
                                            <TextArea
                                                rows={3}
                                                placeholder='贴入接口实际返回的 JSON，自动提取字段。例如：{"temperature": 25, "weather": "sunny"}'
                                                onChange={this.handleJsonOutputChange}
                                            />
                                        </Form.Item>
                                    )}
                                </>
                            ) : (
                                <Alert
                                    type="info"
                                    showIcon
                                    style={{ marginBottom: 16 }}
                                    message="输出字段已根据接口中心的 response schema 自动带入"
                                    description="如需调整，请在接口管理中维护 schema。"
                                />
                            )}

                            <Table
                                size="small"
                                pagination={false}
                                dataSource={this.state.parsedOutputs}
                                rowKey="key"
                                columns={[
                                    { title: '字段名称', dataIndex: 'fieldName', width: 140, render: (v, r) => <Input size="small" value={v} onChange={e => this.handleOutputChange(r.key, 'fieldName', e.target.value)} /> },
                                    { title: '类型', dataIndex: 'paramType', width: 100, render: (v, r) => (
                                        <Select size="small" value={v} onChange={val => this.handleOutputChange(r.key, 'paramType', val)} options={['string','number','integer','boolean','array','object'].map(x => ({label:x, value:x}))} />
                                    )},
                                    { title: '用途描述 (给AI看)', dataIndex: 'description', render: (v, r) => <Input size="small" value={v} onChange={e => this.handleOutputChange(r.key, 'description', e.target.value)} /> },
                                    { title: '', width: 50, render: (_, r) => <Button size="small" danger type="text" icon={<DeleteOutlined/>} onClick={() => this.removeOutput(r.key)} /> }
                                ]}
                            />
                            <Button type="dashed" block size="small" icon={<PlusOutlined />} onClick={this.addEmptyOutput} style={{ marginTop: 8 }}>
                                手工添加一行
                            </Button>
                        </div>

                        {/* 5. 调用策略 */}
                        <div id="advanced-config" style={sectionStyle}>
                            <div style={sectionTitleStyle}>高级配置</div>
                            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#4b5b76' }}>调用策略</div>
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item name="authRequired" label="是否要求鉴权" valuePropName="checked">
                                        <Switch checkedChildren="需要" unCheckedChildren="公开" />
                                    </Form.Item>
                                </Col>
                                <Form.Item name="userContext" hidden valuePropName="checked">
                                    <Switch />
                                </Form.Item>
                                <Form.Item name="maxRows" hidden initialValue={100}>
                                    <InputNumber />
                                </Form.Item>
                            </Row>
                            {this.shouldShowFixedHeaders() ? (
                                <>
                                    <div style={{ marginBottom: 12, fontSize: 13, color: '#4b5b76' }}>固定请求头</div>
                                    <Table
                                        size="small"
                                        pagination={false}
                                        dataSource={this.state.fixedHeaders}
                                        rowKey={(r, idx) => idx}
                                        columns={[
                                            { title: 'Header 名称', dataIndex: 'headerName', width: 220, render: (v, r, idx) => <AutoComplete size="small" value={v} placeholder="如 Authorization" onChange={val => this.handleFixedHeaderChange(idx, 'headerName', val)} options={['Authorization','Content-Type','Accept','X-Request-Id','X-Api-Key','X-Auth-Token','X-User-Id','Cache-Control','User-Agent'].map(h => ({ value: h, label: h }))} filterOption={(input, option) => (option?.value || '').toLowerCase().includes(input.toLowerCase())} style={{ width: '100%' }} /> },
                                            { title: 'Header 值', dataIndex: 'headerValue', render: (v, r, idx) => <Input size="small" value={v} placeholder="如 Bearer xxx" onChange={e => this.handleFixedHeaderChange(idx, 'headerValue', e.target.value)} /> },
                                            { title: '', width: 50, render: (_, r, idx) => <Button size="small" danger type="text" icon={<DeleteOutlined/>} onClick={() => this.removeFixedHeader(idx)} /> }
                                        ]}
                                    />
                                    <Button type="dashed" block size="small" icon={<PlusOutlined />} onClick={this.addFixedHeader} style={{ marginTop: 8 }}>
                                        添加固定请求头
                                    </Button>
                                </>
                            ) : (
                                <div style={{ marginTop: 8 }}>
                                    <Text type="secondary">当前工具无需固定请求头，可留空。</Text>
                                    <Button type="link" icon={<PlusOutlined />} onClick={this.addFixedHeader} style={{ paddingLeft: 8 }}>
                                        需要时再添加固定请求头
                                    </Button>
                                </div>
                            )}
                        </div>

                        {/* 6. 响应映射 */}
                        <div style={{ marginTop: 24, marginBottom: 24 }}>
                            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#4b5b76' }}>响应映射</div>
                            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                                输出字段生成后，这里会自动带出标准映射，无需人工填写。
                            </Text>
                            <Table
                                size="small"
                                pagination={false}
                                dataSource={this.state.responseMappings}
                                rowKey={(r, idx) => idx}
                                locale={{ emptyText: '填写上方输出字段后，这里会自动生成映射' }}
                                columns={[
                                    { title: '输出字段', dataIndex: 'fieldName', width: 180 },
                                    { title: 'JSONPath', dataIndex: 'jsonPath', render: v => <Text code>{v}</Text> }
                                ]}
                            />
                        </div>

                        {/* 7. 示例 */}
                        <div>
                            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#4b5b76' }}>调用示例</div>
                            <Table
                                size="small"
                                pagination={false}
                                dataSource={this.state.examples}
                                rowKey={(r, idx) => idx}
                                locale={{ emptyText: '暂无数据' }}
                                columns={[
                                    { title: '示例名', dataIndex: 'exampleName', width: 160, render: (v, r, idx) => <Input size="small" value={v} onChange={e => this.handleExampleChange(idx, 'exampleName', e.target.value)} /> },
                                    { title: '用户问题', dataIndex: 'userQuery', width: 200, render: (v, r, idx) => <Input size="small" value={v} onChange={e => this.handleExampleChange(idx, 'userQuery', e.target.value)} /> },
                                    { title: '输入 JSON', dataIndex: 'inputJson', render: (v, r, idx) => <TextArea rows={3} value={v} onChange={e => this.handleExampleChange(idx, 'inputJson', e.target.value)} /> },
                                    { title: '输出 JSON', dataIndex: 'outputJson', render: (v, r, idx) => <TextArea rows={3} value={v} onChange={e => this.handleExampleChange(idx, 'outputJson', e.target.value)} /> },
                                    { title: '', width: 50, render: (_, r, idx) => <Button size="small" danger type="text" icon={<DeleteOutlined/>} onClick={() => this.removeExample(idx)} /> }
                                ]}
                            />
                            <Button type="dashed" block size="small" icon={<PlusOutlined />} onClick={this.addExample} style={{ marginTop: 8 }}>
                                添加示例
                            </Button>
                        </div>

                            {this.state.editRecord && (
                                <div style={{ marginTop: 20 }}>
                                    <Button icon={<CodeOutlined/>} onClick={this.previewSpec}>
                                        预览当前版本 Spec
                                    </Button>
                                </div>
                            )}
                            </Col>
                            <Col flex="180px">
                            <div style={{ position: 'sticky', top: 96 }}>
                                <div style={{ background: '#fff', border: '1px solid #edf0f5', borderRadius: 16, padding: 16 }}>
                                    {editorSections.map(item => (
                                        <div
                                            key={item.key}
                                            onClick={() => this.scrollToEditorSection(item.key)}
                                            style={{
                                                cursor: 'pointer',
                                                padding: '10px 12px',
                                                borderLeft: activeEditorSection === item.key ? '3px solid #1677ff' : '3px solid transparent',
                                                color: activeEditorSection === item.key ? '#1677ff' : '#5b6577',
                                                fontWeight: activeEditorSection === item.key ? 600 : 500,
                                                marginBottom: 6
                                            }}
                                        >
                                            {item.label}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            </Col>
                        </Row>
                    </Form>
                </Drawer>

                <Modal
                    title="工具调试"
                    visible={toolDebugVisible}
                    onCancel={this.closeToolDebugModal}
                    footer={null}
                    width={900}
                    destroyOnClose
                >
                    <div style={{ background: '#f7f9fc', borderRadius: 16, padding: 24 }}>
                        <Space style={{ marginBottom: 16 }}>
                            <Button
                                type={toolDebugInputMode === 'form' ? 'primary' : 'default'}
                                onClick={() => this.setState({ toolDebugInputMode: 'form' })}
                            >
                                输入参数配置
                            </Button>
                            <Button
                                type={toolDebugInputMode === 'json' ? 'primary' : 'default'}
                                onClick={() => this.setState({ toolDebugInputMode: 'json' })}
                            >
                                代码模式编辑
                            </Button>
                        </Space>

                        {toolDebugInputMode === 'form' ? (
                            toolDebugFields.length ? (
                                <div>
                                    {toolDebugFields.map(field => (
                                        <div key={field.key} style={{ display: 'flex', gap: 16, marginBottom: 16, alignItems: field.paramType === 'array' || field.paramType === 'object' ? 'flex-start' : 'center' }}>
                                            <div style={{ width: 280, paddingTop: 6 }}>
                                                <div style={{ fontSize: 15, fontWeight: 600, color: '#344054' }}>
                                                    {field.required ? <span style={{ color: '#ff4d4f', marginRight: 4 }}>*</span> : null}
                                                    {field.label}
                                                    <Tag color="blue" style={{ marginLeft: 8 }}>{field.paramType}</Tag>
                                                </div>
                                                <div style={{ color: '#98a2b3', fontSize: 12, marginTop: 4 }}>
                                                    {field.location ? `${field.location.toUpperCase()} 参数` : '参数'}{field.description ? ` · ${field.description}` : ''}
                                                </div>
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                {field.paramType === 'boolean' ? (
                                                    <Select
                                                        size="large"
                                                        value={toolDebugParamValues[field.key]}
                                                        placeholder="请选择 true / false"
                                                        onChange={value => this.handleToolDebugParamValueChange(field.key, value)}
                                                        options={[
                                                            { label: 'true', value: 'true' },
                                                            { label: 'false', value: 'false' }
                                                        ]}
                                                    />
                                                ) : field.paramType === 'array' || field.paramType === 'object' ? (
                                                    <TextArea
                                                        rows={3}
                                                        value={toolDebugParamValues[field.key]}
                                                        placeholder={field.paramType === 'array' ? '请输入 JSON 数组，例如 [1,2,3]' : '请输入 JSON 对象，例如 {"id":1}'}
                                                        onChange={e => this.handleToolDebugParamValueChange(field.key, e.target.value)}
                                                    />
                                                ) : (
                                                    <Input
                                                        size="large"
                                                        value={toolDebugParamValues[field.key]}
                                                        placeholder="请输入值"
                                                        onChange={e => this.handleToolDebugParamValueChange(field.key, e.target.value)}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <Alert
                                    type="info"
                                    showIcon
                                    message="当前没有可直接编辑的顶层参数"
                                    description="可以切到“代码模式编辑”，直接输入完整 JSON 调试请求。"
                                />
                            )
                        ) : (
                            <div>
                                <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                                    直接输入完整 JSON 对象作为调试入参。路径参数、Query 参数、Header 参数会根据上面的参数定义自动拆分。
                                </Text>
                                <TextArea
                                    rows={10}
                                    value={toolDebugCodeInput}
                                    onChange={e => this.handleToolDebugCodeInputChange(e.target.value)}
                                    placeholder={`{
  "id": 1
}`}
                                />
                            </div>
                        )}

                        <div style={{ marginTop: 20 }}>
                            <Button type="primary" size="large" loading={debugging} onClick={this.handleToolDebugRun}>
                                开始运行
                            </Button>
                        </div>

                        {toolDebugRunResult && (
                            <div style={{ marginTop: 16 }}>
                                <Alert
                                    type={toolDebugRunResult.success ? 'success' : 'error'}
                                    showIcon
                                    message={toolDebugRunResult.message}
                                    description={toolDebugRunResult.description}
                                    style={{ marginBottom: toolDebugRunResult.data ? 12 : 0 }}
                                />
                                {toolDebugRunResult.data && (
                                    <div style={{ background: '#fff', border: '1px solid #e4e7ec', padding: 12, borderRadius: 12, maxHeight: 280, overflow: 'auto' }}>
                                        <pre style={{ margin: 0, fontSize: 12 }}>{JSON.stringify(toolDebugRunResult.data, null, 2)}</pre>
                                    </div>
                                )}
                                {toolDebugRunResult.schemaCompare && (
                                    <Alert
                                        style={{ marginTop: 12 }}
                                        type={toolDebugRunResult.schemaCompare.matched ? 'success' : 'warning'}
                                        showIcon
                                        message={toolDebugRunResult.schemaCompare.summary}
                                        description={[
                                            toolDebugRunResult.schemaCompare.missingFields?.length
                                                ? `Schema 缺少字段：${toolDebugRunResult.schemaCompare.missingFields.join(' / ')}`
                                                : '',
                                            toolDebugRunResult.schemaCompare.extraFields?.length
                                                ? `响应多出字段：${toolDebugRunResult.schemaCompare.extraFields.join(' / ')}`
                                                : ''
                                        ].filter(Boolean).join('；') || '返回结构已覆盖接口中心定义的输出字段。'}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                </Modal>

                {/* ======== 测试结果弹窗 ======== */}
                <Modal
                    title={<span><BugOutlined /> 工具测试结果</span>}
                    visible={testModalVisible}
                    onCancel={() => this.setState({ testModalVisible: false, testResult: null, testTargetRecord: null })}
                    footer={
                        <Space>
                            {testResult && !testResult.success && testTargetRecord?.id && (
                                <Button type="primary" ghost onClick={this.openTestTargetEditor}>去设置</Button>
                            )}
                            <Button onClick={() => this.setState({ testModalVisible: false, testResult: null, testTargetRecord: null })}>关闭</Button>
                        </Space>
                    }
                    width={600}
                >
                    {testing && <div style={{ textAlign: 'center', padding: 40 }}><Spin tip="正在调用后端测试..." /></div>}
                    {testResult && !testing && (
                        <div>
                            <Alert
                                type={testResult.success ? 'success' : 'error'}
                                showIcon
                                message={testResult.message}
                                description={testResult.description}
                                style={{ marginBottom: 16 }}
                            />
                            {testResult.data && (
                                <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 8, maxHeight: 300, overflow: 'auto' }}>
                                    <pre style={{ margin: 0, fontSize: 12 }}>{JSON.stringify(testResult.data, null, 2)}</pre>
                                </div>
                            )}
                        </div>
                    )}
                </Modal>

                <Modal
                    title={<span><CodeOutlined /> Spec 预览</span>}
                    visible={this.state.specPreviewVisible}
                    onCancel={() => this.setState({ specPreviewVisible: false })}
                    footer={<Button onClick={() => this.setState({ specPreviewVisible: false })}>关闭</Button>}
                    width={860}
                >
                    {this.state.specPreviewLoading
                        ? <div style={{ textAlign: 'center', padding: 40 }}><Spin tip="正在加载 spec..." /></div>
                        : (
                            <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 8, maxHeight: 520, overflow: 'auto' }}>
                                <pre style={{ margin: 0, fontSize: 12 }}>{JSON.stringify(this.state.specPreviewData, null, 2)}</pre>
                            </div>
                        )}
                </Modal>

                {/* ======== 发布成功 - 连接信息弹窗 ======== */}
                <Modal
                    title={<span><CheckCircleOutlined style={{ color: '#52c41a' }} /> 发布成功 - MCP 接入指南</span>}
                    visible={publishInfoVisible}
                    onCancel={() => this.setState({ publishInfoVisible: false })}
                    footer={<Button type="primary" onClick={() => this.setState({ publishInfoVisible: false })}>知道了</Button>}
                    width={640}
                >
                    {publishedTool && (
                        <div>
                            <Alert type="success" showIcon message={`工具「${publishedTool.name}」已成功发布上线！`} style={{ marginBottom: 16 }} />

                            <div style={{ fontWeight: 600, marginBottom: 8 }}>MCP Server SSE Endpoint</div>
                            <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 8, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <code style={{ fontSize: 13, wordBreak: 'break-all' }}>{mcpEndpoint || '请从服务管理页进入以获取完整地址'}</code>
                                {mcpEndpoint && <Button size="small" icon={<CopyOutlined />} onClick={() => this.copyToClipboard(mcpEndpoint)}>复制</Button>}
                            </div>

                            <div style={{ fontWeight: 600, marginBottom: 8 }}>客户端配置示例 (Claude Desktop / Cursor)</div>
                            <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 8, position: 'relative' }}>
                                <pre style={{ margin: 0, fontSize: 12 }}>{JSON.stringify({
                                    mcpServers: {
                                        [serviceInfo?.serviceCode || 'my-service']: {
                                            url: mcpEndpoint || '<MCP_SSE_ENDPOINT>',
                                            headers: {
                                                'X-API-Key': '<your-api-key>'
                                            }
                                        }
                                    }
                                }, null, 2)}</pre>
                                <Button size="small" icon={<CopyOutlined />}
                                    style={{ position: 'absolute', top: 8, right: 8 }}
                                    onClick={() => this.copyToClipboard(JSON.stringify({
                                        mcpServers: {
                                            [serviceInfo?.serviceCode || 'my-service']: {
                                                url: mcpEndpoint || '<MCP_SSE_ENDPOINT>',
                                                headers: { 'X-API-Key': '<your-api-key>' }
                                            }
                                        }
                                    }, null, 2))}>复制</Button>
                            </div>

                            <Alert type="info" showIcon style={{ marginTop: 16 }}
                                message="API Key 可在「API Key 管理」页面生成和管理"
                            />
                        </div>
                    )}
                </Modal>
            </div>
        )
    }
}
